import React, { useContext, useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import ReactDOM from "react-dom";
import LanguageContext from "../helper/LanguageContext";
import { KJUUP_COLORS, API_ROOT } from "../helper/constants";
import MaterialIcon from "./MaterialIcon";
import { callAPI } from "../helper/apiUtils";
import { $crud } from "../helper/CrudFactory";
import EmailRecipientInput from "./EmailRecipientInput";
import ComposeProjectField from "./ComposeProjectField";
import ProjectTagActionModal from "./ProjectTagActionModal";
import { getProjectTagColors } from "../utils/projectTagColor";
import { findExactProject } from "../utils/projectMatch";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { looksLikeHtml, getEmailAccountId, pickOwnedEmailAccountId, pickComposeAccountId, preferLongerText, formatEmailSenderDisplay, mergeEnrichedEmail, isPersistedEmailId, formatEmailPlainBody, isCollapsedEmailStub, repairMashedEmailHtml, linkifyPlainText, linkifyElement, hydrateComposeEditorBody, handleComposeLinkifyInput, handleComposeLinkClick, emailsFromRecipientField } from "../helper/emailBodyFormat";
import {
  findAccountById,
  signatureForAccount,
  applyEmailSignatureToEditor,
  editorLooksEmpty,
  composePlainWithoutSignature,
} from "../helper/emailSignature";
import { promptSaveDraft } from "../helper/emailDraftClosePrompt";
import { draftAttachmentsOf, draftAttachmentsField, draftRequestBody } from "../helper/emailDraftAttachments";
import { attachEmailChatLinkHandler, isKjuupChatOpenHref } from "../helper/chatDeeplink";
import EmailReplySuggestions, { insertEmailReplySuggestion, editorHasUserDraft } from "./EmailReplySuggestions";
import { emailProjectIdOf } from "../helper/bookmarkTodo";
import "./EmailDetailView.css";

/** Force a true file download (never open in a browser preview tab). */
function triggerBlobDownload(blob, filename) {
  const downloadBlob = blob.type === "application/octet-stream"
    ? blob
    : new Blob([blob], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(downloadBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "download";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Delay revoke so the browser can start the download first.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

function uniqueZipEntryName(filename, index, usedNames) {
  const raw = (filename && String(filename).trim()) || `attachment-${index + 1}`;
  const name = raw.replace(/[\\/:*?"<>|]/g, "_");
  let candidate = name;
  let counter = 1;
  while (usedNames.has(candidate.toLowerCase())) {
    const dot = name.lastIndexOf(".");
    const base = dot === -1 ? name : name.slice(0, dot);
    const ext = dot === -1 ? "" : name.slice(dot);
    candidate = `${base} (${counter})${ext}`;
    counter++;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
}

function sanitizeZipFilename(subject) {
  const base = (subject && String(subject).trim()) || "attachments";
  const cleaned = base.replace(/[\\/:*?"<>|]/g, "_").replace(/\s+/g, " ").trim().slice(0, 80);
  return `${cleaned || "attachments"}.zip`;
}

function hasVisibleContent(html) {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim();
  return stripped.length > 0;
}

function isDailyBriefingEmail(email) {
  if (!email) return false;
  if (email.systemTag === "daily_briefing") return true;
  const from = email.fromAddress || {};
  const fromEmail = String(from.email || from.address || email.from || "").toLowerCase();
  const subject = String(email.subject || "");
  return fromEmail === "info@kjuup.de" && /tages[-\s]?briefing|tages[-\s]?bericht|daily\s*(briefing|report)/i.test(subject);
}

function stripMailHeaderPreamble(html) {
  if (!html) return html;
  return String(html)
    .replace(
      /(?:<p[^>]*>\s*)?(?:<strong>)?(From|To|Date|Sent|Subject|Von|An|Datum|Gesendet|Betreff)(?:<\/strong>)?:\s*[\s\S]*?(?:<\/p>|<br\s*\/?>)/gi,
      ""
    );
}

function strippedBodyLen(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim().length;
}

// Same rule as the backend's isThinOriginal: only list rows that carry just
// the snippet need a refetch before quoting them in a reply.
function hasFullBody(email) {
  if (!email) return false;
  const len = Math.max(
    strippedBodyLen(email.bodyHtml || email.html),
    strippedBodyLen(email.bodyText)
  );
  if (len < 80) return false;
  return len > strippedBodyLen(email.snippet) + 40;
}

function resolveEmailBody(email) {
  if (!email) return "";
  const html = email.bodyHtml || email.html || "";
  const text = email.bodyText || "";
  const fallback = email.body || email.snippet || "";
  // Newsletters (e.g. MyNotary) ship a designed HTML part and a much longer
  // text/plain alternative full of tracking URLs. Prefer real HTML so the
  // iframe shows the layout instead of linkified URLs.
  if (looksLikeHtml(html) && hasVisibleContent(html) && !isCollapsedEmailStub(html)) {
    return repairMashedEmailHtml(html);
  }
  if (looksLikeHtml(text) && hasVisibleContent(text) && !isCollapsedEmailStub(text)) {
    return repairMashedEmailHtml(text);
  }
  const preferred = [text, html, fallback].find((v) => String(v || "").trim()) || "";
  return formatEmailPlainBody(preferred);
}

// Render a list of recipients (`[{ name, email }, ...]`) as a comma-joined
// "Name <email>" string. Accepts either the structured array stored on
// EmailMessage (`toAddresses` / `ccAddresses` / `bccAddresses`) or a
// preformatted fallback string used in some payloads (e.g. search results).
function formatRecipientList(addresses, fallbackString) {
  if (Array.isArray(addresses) && addresses.length > 0) {
    const parts = addresses
      .map((a) => {
        if (!a) return "";
        if (typeof a === "string") return a.trim();
        const name = a.name && String(a.name).trim();
        const addr = a.email && String(a.email).trim();
        if (name && addr) return `${name} <${addr}>`;
        return name || addr || "";
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join(", ");
  }
  if (typeof fallbackString === "string" && fallbackString.trim()) {
    return fallbackString.trim();
  }
  return "";
}

function makeLinksOpenNewTab(doc) {
  const anchors = doc.querySelectorAll("a[href]");
  anchors.forEach((a) => {
    const href = a.getAttribute("href") || "";
    // Chat notification CTAs are handled in-app; keep them in this tab.
    if (isKjuupChatOpenHref(href)) {
      a.setAttribute("target", "_self");
      a.removeAttribute("rel");
      return;
    }
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
}

function prepareEmailHtml(html) {
  const styles = [];
  let body = html;

  body = body.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, (s) => { styles.push(s); return ''; });
  body = body.replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '');
  body = body.replace(/<meta[^>]*\/?>/gi, '');
  body = body.replace(/<link[^>]*\/?>/gi, '');
  body = body.replace(/<\/?(html|head|body)(?:\s[^>]*)?>/gi, '');

  return { styles: styles.join('\n'), body: body.trim() };
}

/**
 * Post-processes the email DOM inside the iframe to visually format
 * conversation history (quoted replies) in an Outlook-like style with
 * separators and styled header blocks.
 */
function formatEmailConversationHistory(doc) {
  const body = doc.body;
  if (!body) return;

  const SEPARATOR_PATTERNS = [
    /^-{3,}\s*(Original Message|Urspr[üu]ngliche Nachricht|Message d'origine|Mensaje original|Messaggio originale|Oorspronkelijk bericht|Wiadomo[śs][ćc] oryginalna|Mensagem original|Mesaj orijinal)\s*-{3,}$/i,
    /^_{3,}\s*$/,
    /^-{5,}\s*$/,
    /^_{5,}\s*$/,
  ];

  const HEADER_LINE_PATTERNS = [
    /^(From|Von|De|Da|Van|Od|Kimden)\s*:/i,
    /^(Sent|Gesendet|Envoy[ée]|Enviado|Inviato|Verzonden|Wys[łl]ano|Enviado em|G[öo]nderildi)\s*:/i,
    /^(To|An|[ÀA]|A|Para|Aan|Do|Para|Kime)\s*:/i,
    /^(Subject|Betreff|Objet|Asunto|Oggetto|Onderwerp|Temat|Assunto|Konu)\s*:/i,
    /^(Cc|CC)\s*:/i,
    /^(Date|Datum|Fecha|Data|Tarih)\s*:/i,
  ];

  function isSeparatorLine(text) {
    const trimmed = text.trim();
    return SEPARATOR_PATTERNS.some(p => p.test(trimmed));
  }

  function isHeaderLine(text) {
    const trimmed = text.trim();
    return HEADER_LINE_PATTERNS.some(p => p.test(trimmed));
  }

  function formatHeaderBlock(headerLines) {
    const formatted = headerLines.map(line => {
      const colonIdx = line.indexOf(":");
      if (colonIdx > -1) {
        const label = line.substring(0, colonIdx).trim();
        const value = line.substring(colonIdx + 1).trim();
        return `<span class="email-quote-label">${label}:</span> <strong>${escapeHtmlText(value)}</strong>`;
      }
      return escapeHtmlText(line);
    });
    return `<div class="email-quote-header">${formatted.join("<br>")}</div>`;
  }

  function escapeHtmlText(text) {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  function processPlainTextNode(node) {
    const text = node.textContent || "";
    const lines = text.split(/\n/);
    let i = 0;
    const fragments = [];
    let currentBlock = [];

    while (i < lines.length) {
      const line = lines[i];
      if (isSeparatorLine(line)) {
        if (currentBlock.length > 0) {
          fragments.push({ type: "text", content: currentBlock.join("\n") });
          currentBlock = [];
        }
        const headerLines = [];
        i++;
        while (i < lines.length && (isHeaderLine(lines[i]) || lines[i].trim() === "")) {
          if (lines[i].trim() !== "") headerLines.push(lines[i].trim());
          i++;
        }
        fragments.push({ type: "separator", headers: headerLines });
      } else if (i > 0 && isHeaderLine(line) && !currentBlock.some(l => l.trim().length > 0 && !isHeaderLine(l.trim()))) {
        const headerLines = [];
        while (i < lines.length && (isHeaderLine(lines[i]) || lines[i].trim() === "")) {
          if (lines[i].trim() !== "") headerLines.push(lines[i].trim());
          i++;
        }
        if (headerLines.length >= 2) {
          if (currentBlock.length > 0) {
            fragments.push({ type: "text", content: currentBlock.join("\n") });
            currentBlock = [];
          }
          fragments.push({ type: "separator", headers: headerLines });
        } else {
          currentBlock.push(...headerLines);
        }
      } else {
        currentBlock.push(line);
        i++;
      }
    }
    if (currentBlock.length > 0) {
      fragments.push({ type: "text", content: currentBlock.join("\n") });
    }

    if (fragments.length <= 1) return false;

    const container = doc.createElement("div");
    fragments.forEach(f => {
      if (f.type === "separator") {
        const hr = doc.createElement("hr");
        hr.className = "email-quote-separator";
        container.appendChild(hr);
        if (f.headers.length > 0) {
          const headerDiv = doc.createElement("div");
          headerDiv.innerHTML = formatHeaderBlock(f.headers);
          container.appendChild(headerDiv.firstChild);
        }
      } else {
        const pre = doc.createElement("pre");
        pre.style.whiteSpace = "pre-wrap";
        pre.style.fontFamily = "inherit";
        pre.style.margin = "0";
        pre.textContent = f.content;
        container.appendChild(pre);
      }
    });

    node.parentNode.replaceChild(container, node);
    return true;
  }

  function processHtmlBody() {
    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      const text = node.textContent || "";
      if (SEPARATOR_PATTERNS.some(p => p.test(text.trim()))) {
        textNodes.push(node);
      }
    }

    textNodes.forEach(textNode => {
      const parent = textNode.parentNode;
      if (!parent) return;
      const container = parent.closest ? parent.closest("div, td, p, blockquote, body") : body;
      if (!container) return;

      const hr = doc.createElement("hr");
      hr.className = "email-quote-separator";

      const headerLines = [];
      let sibling = textNode.parentNode === container ? textNode.nextSibling : parent.nextSibling;

      const tempCheck = [];
      let checkCount = 0;
      while (sibling && checkCount < 8) {
        const sibText = (sibling.textContent || "").trim();
        if (sibText === "") {
          sibling = sibling.nextSibling;
          checkCount++;
          continue;
        }
        if (isHeaderLine(sibText)) {
          tempCheck.push(sibling);
          headerLines.push(sibText);
        } else {
          break;
        }
        sibling = sibling.nextSibling;
        checkCount++;
      }

      if (headerLines.length >= 2) {
        const headerDiv = doc.createElement("div");
        headerDiv.className = "email-quote-header";
        headerLines.forEach(line => {
          const colonIdx = line.indexOf(":");
          if (colonIdx > -1) {
            const label = line.substring(0, colonIdx).trim();
            const value = line.substring(colonIdx + 1).trim();
            const span = doc.createElement("span");
            span.innerHTML = `<span class="email-quote-label">${label}:</span> <strong>${value.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</strong>`;
            headerDiv.appendChild(span);
            headerDiv.appendChild(doc.createElement("br"));
          }
        });

        const insertBefore = textNode.parentNode === container ? textNode : parent;
        container.insertBefore(hr, insertBefore);
        container.insertBefore(headerDiv, insertBefore);

        if (textNode.parentNode) textNode.parentNode.removeChild(textNode);
        tempCheck.forEach(el => {
          if (el.parentNode) el.parentNode.removeChild(el);
        });
      }
    });
  }

  function processInlineTextBlocks() {
    const allElements = body.querySelectorAll("div, p, td, span");
    allElements.forEach(el => {
      if (el.querySelector("div, p, td, pre, iframe")) return;
      const text = el.textContent || "";
      const lines = text.split(/\n/);
      if (lines.length < 3) return;
      let hasSeparator = false;
      for (const line of lines) {
        if (SEPARATOR_PATTERNS.some(p => p.test(line.trim()))) { hasSeparator = true; break; }
      }
      if (!hasSeparator) {
        let consecutiveHeaders = 0;
        for (const line of lines) {
          if (isHeaderLine(line)) { consecutiveHeaders++; if (consecutiveHeaders >= 3) break; }
          else if (line.trim()) consecutiveHeaders = 0;
        }
        if (consecutiveHeaders < 3) return;
      }
      if (processPlainTextNode(el)) return;
    });
  }

  const preElements = body.querySelectorAll("pre");
  let processedPlain = false;
  preElements.forEach(pre => {
    if (processPlainTextNode(pre)) processedPlain = true;
  });

  if (!processedPlain) {
    processHtmlBody();
    processInlineTextBlocks();
  }
}

function cleanupEmailImages(doc) {
  const images = doc.querySelectorAll("img");
  images.forEach((img) => {
    img.removeAttribute("alt");
    img.removeAttribute("title");
    // Keep the shape the email asked for (e.g. a 80×80 avatar) when the width has to shrink:
    // the box follows the declared ratio and the height follows the width, instead of a fixed
    // height squeezing the picture. object-fit (styles) keeps the picture itself undistorted.
    // Pixel values only ("80" / "80px"); a "100%" width says nothing about the shape.
    const px = (value) => (/^\s*\d+(\.\d+)?\s*(px)?\s*$/i.test(value || "") ? parseFloat(value) : 0);
    const w = px(img.getAttribute("width"));
    const h = px(img.getAttribute("height"));
    if (w > 1 && h > 1) {
      img.style.setProperty("aspect-ratio", `${w} / ${h}`);
      img.style.setProperty("height", "auto", "important");
    }
  });
}

const PREVIEWABLE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "message/rfc822",
  "application/vnd.ms-outlook",
];

const OFFICE_WORD_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const OFFICE_EXCEL_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const OUTLOOK_TYPES = [
  "message/rfc822",
  "application/vnd.ms-outlook",
];

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!n || n <= 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return (n / Math.pow(k, i)).toFixed(1) + " " + sizes[i];
}

function getFileIconName(mimeType) {
  if (!mimeType) return "description";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "picture_as_pdf";
  if (mimeType.startsWith("text/")) return "article";
  if (OFFICE_WORD_TYPES.includes(mimeType)) return "article";
  if (OFFICE_EXCEL_TYPES.includes(mimeType)) return "grid_on";
  if (OUTLOOK_TYPES.includes(mimeType)) return "mail";
  return "description";
}

async function convertWordToHtml(arrayBuffer) {
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return result.value;
}

function convertExcelToHtml(arrayBuffer) {
  const data = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer);
  const workbook = XLSX.read(data, { type: "array", cellDates: true });
  if (!workbook || !Array.isArray(workbook.SheetNames) || workbook.SheetNames.length === 0) {
    return [];
  }
  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name];
    const html = XLSX.utils.sheet_to_html(sheet || {}, { editable: false });
    return { name, html: html || "" };
  });
}

function parseEml(text) {
  const headerEnd = text.indexOf("\r\n\r\n");
  const splitAt = headerEnd > -1 ? headerEnd : text.indexOf("\n\n");
  const headerBlock = splitAt > -1 ? text.substring(0, splitAt) : "";
  const body = splitAt > -1 ? text.substring(splitAt).trim() : text;

  const getHeader = (name) => {
    const re = new RegExp(`^${name}:\\s*(.+)`, "im");
    const m = headerBlock.match(re);
    return m ? m[1].trim() : "";
  };

  return {
    from: getHeader("From"),
    to: getHeader("To"),
    subject: getHeader("Subject"),
    date: getHeader("Date"),
    body,
  };
}

/** Trailing !important rules so the sender's stylesheet cannot pin html/body to
 *  100% height / overflow:hidden (that makes scrollHeight === viewport and
 *  clips the message). Must be injected AFTER extracted email <style> tags. */
const EMAIL_IFRAME_OVERFLOW_OVERRIDE = `
  html, body {
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }
  /* Senders ship CSS that blocks selection; this block is injected after the
     email's own <style>, so the reader can always select and copy the text. */
  html, body, body * {
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
    user-select: text !important;
    -webkit-touch-callout: default !important;
  }
  img[width="48"][height="48"] {
    width: 48px !important;
    height: 48px !important;
    max-width: 48px !important;
    max-height: 48px !important;
    object-fit: cover !important;
    object-position: center !important;
    border-radius: 24px !important;
  }
`;

function buildEmailIframeDocument({ styles = "", body = "", fontSize = "13px", bodyPadding = "20px" } = {}) {
  return `<!DOCTYPE html>
<html>
<head>
  <style>${buildEmailIframeDocStyles({ fontSize, bodyPadding })}</style>
  ${styles}
  <style>${EMAIL_IFRAME_OVERFLOW_OVERRIDE}</style>
</head>
<body>${body}</body>
</html>`;
}

function findEmailScrollParent(start) {
  let node = start && start.parentElement;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** Wheel/touch/keys over a scrolling="no" iframe never reach the outer pane.
 *  Relay them to `.email-detail-scroll` / `.email-inline-reply-scroll`. */
function attachIframeScrollRelay(iframe) {
  if (!iframe) return () => {};
  let doc;
  let win;
  try {
    doc = iframe.contentDocument || iframe.contentWindow?.document;
    win = iframe.contentWindow;
  } catch (e) {
    return () => {};
  }
  if (!doc) return () => {};

  const getParent = () => findEmailScrollParent(iframe);

  const onWheel = (event) => {
    const parent = getParent();
    if (!parent) return;
    parent.scrollTop += event.deltaY;
    parent.scrollLeft += event.deltaX;
    event.preventDefault();
  };

  /** True once a long-press has put a selection in the message: from then on the
   *  drag belongs to the selection handles, not to our scroll relay. */
  const isSelecting = () => {
    const sel = doc.getSelection && doc.getSelection();
    return !!sel && !sel.isCollapsed;
  };

  let lastTouchY = 0;
  const onTouchStart = (event) => {
    if (event.touches && event.touches.length) lastTouchY = event.touches[0].clientY;
  };
  const onTouchMove = (event) => {
    if (!event.touches || !event.touches.length) return;
    if (isSelecting()) return;
    const parent = getParent();
    if (!parent) return;
    const y = event.touches[0].clientY;
    parent.scrollTop += lastTouchY - y;
    lastTouchY = y;
    event.preventDefault();
  };

  const onKeyDown = (event) => {
    const tag = (event.target && event.target.tagName) || "";
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    const parent = getParent();
    if (!parent) return;
    const page = Math.max(parent.clientHeight * 0.9, 40);
    let delta = 0;
    if (event.key === "ArrowDown") delta = 40;
    else if (event.key === "ArrowUp") delta = -40;
    else if (event.key === "PageDown") delta = page;
    else if (event.key === "PageUp") delta = -page;
    else if (event.key === "Home") {
      parent.scrollTop = 0;
      event.preventDefault();
      return;
    } else if (event.key === "End") {
      parent.scrollTop = parent.scrollHeight;
      event.preventDefault();
      return;
    } else if (event.key === " " && !event.shiftKey) delta = page;
    else if (event.key === " " && event.shiftKey) delta = -page;
    if (!delta) return;
    parent.scrollTop += delta;
    event.preventDefault();
  };

  const wheelOpts = { passive: false };
  doc.addEventListener("wheel", onWheel, wheelOpts);
  doc.addEventListener("touchstart", onTouchStart, { passive: true });
  doc.addEventListener("touchmove", onTouchMove, wheelOpts);
  doc.addEventListener("keydown", onKeyDown);
  if (win && win !== window) {
    try {
      win.addEventListener("wheel", onWheel, wheelOpts);
      win.addEventListener("keydown", onKeyDown);
    } catch (e) {
      /* ignore */
    }
  }

  return () => {
    try {
      doc.removeEventListener("wheel", onWheel);
      doc.removeEventListener("touchstart", onTouchStart);
      doc.removeEventListener("touchmove", onTouchMove);
      doc.removeEventListener("keydown", onKeyDown);
      if (win && win !== window) {
        win.removeEventListener("wheel", onWheel);
        win.removeEventListener("keydown", onKeyDown);
      }
    } catch (e) {
      /* document may already be gone */
    }
  };
}

function combineCleanups(...fns) {
  return () => {
    fns.forEach((fn) => {
      if (typeof fn === "function") fn();
    });
  };
}

/** Newsletters often ship fixed-width layouts (e.g. a 650px table with a pinned
 *  td width) that max-width cannot shrink, so their right edge was clipped by the
 *  iframe. Scale the body down until the content fits, like mail clients do. */
function fitIframeContentToWidth(iframe, doc) {
  const html = doc.documentElement;
  const body = doc.body;
  const available = iframe.clientWidth || html.clientWidth;
  if (!available) return;
  body.style.removeProperty("zoom");
  let scale = 1;
  for (let i = 0; i < 3; i++) {
    const contentWidth = Math.max(html.scrollWidth || 0, body.scrollWidth || 0);
    if (contentWidth <= available + 1) break;
    scale = Math.max(0.35, scale * (available / contentWidth));
    body.style.setProperty("zoom", String(scale), "important");
    if (scale === 0.35) break;
  }
}

/** Match iframe height to document content so the panel scrolls once, not inside the iframe.
 *  Width is always pinned to 100% to prevent horizontal overflow of the details panel. */
function resizeIframeToContent(iframe) {
  if (!iframe) return () => {};
  let cancelled = false;
  const timeouts = [];
  let observer = null;

  const measure = () => {
    if (cancelled) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc?.body) return;
      const html = doc.documentElement;
      const body = doc.body;
      html.style.setProperty("height", "auto", "important");
      body.style.setProperty("height", "auto", "important");
      html.style.setProperty("overflow", "visible", "important");
      body.style.setProperty("overflow", "visible", "important");
      fitIframeContentToWidth(iframe, doc);

      let contentBottom = 0;
      const rootTop = body.getBoundingClientRect().top;
      const nodes = body.querySelectorAll("*");
      const limit = Math.min(nodes.length, 4000);
      for (let i = 0; i < limit; i++) {
        const bottom = nodes[i].getBoundingClientRect().bottom;
        if (bottom > contentBottom) contentBottom = bottom;
      }
      const paddingBottom = parseFloat((doc.defaultView || window).getComputedStyle(body).paddingBottom) || 0;
      const byRects = Math.ceil(contentBottom - rootTop + paddingBottom);
      const h = Math.max(
        byRects,
        html.scrollHeight || 0,
        body.scrollHeight || 0,
        html.offsetHeight || 0,
        body.offsetHeight || 0,
        80
      );
      iframe.style.height = `${h}px`;
      iframe.style.width = "100%";
    } catch (e) {
      /* ignore */
    }
  };

  measure();
  requestAnimationFrame(() => requestAnimationFrame(measure));
  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc?.body) {
      const imgs = doc.getElementsByTagName("img");
      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        if (!img.complete) img.addEventListener("load", measure, { once: true });
      }
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(measure);
        observer.observe(doc.body);
        if (doc.documentElement) observer.observe(doc.documentElement);
      }
    }
  } catch (e) {
    /* ignore */
  }
  timeouts.push(setTimeout(measure, 200));
  timeouts.push(setTimeout(measure, 800));
  timeouts.push(setTimeout(measure, 2000));

  return () => {
    cancelled = true;
    timeouts.forEach(clearTimeout);
    if (observer) {
      try { observer.disconnect(); } catch (e) { /* ignore */ }
    }
  };
}

function buildEmailIframeDocStyles({ fontSize = "13px", bodyPadding = "20px" } = {}) {
  return `
  /* overflow:visible on BOTH axes. overflow-x:hidden + overflow-y:visible is
     computed as overflow-y:auto (CSS overflow pairing), which creates an inner
     iframe scrollport that scrolling="no" then locks — the message cannot scroll. */
  html {
    margin: 0;
    padding: 0;
    height: auto;
    overflow: visible;
    max-width: 100%;
    text-size-adjust: 100%;
    -webkit-text-size-adjust: 100%;
  }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: ${fontSize};
    line-height: 1.45;
    color: #333;
    padding: ${bodyPadding};
    margin: 0;
    height: auto;
    overflow: visible;
    max-width: 100%;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }

  /* Force the message text down to the Kjuup chat-message size regardless of the
     inline / embedded font-sizes the original email ships with. !important in the
     stylesheet overrides non-important inline styles and legacy <font size> attrs.
     Headings keep a small hierarchy so structured emails stay readable. */
  body, p, div, span, td, th, li, ul, ol, a, font, small, label, blockquote, pre, code, table {
    font-size: ${fontSize} !important;
  }
  h1 { font-size: 18px !important; }
  h2 { font-size: 16px !important; }
  h3 { font-size: 15px !important; }
  h4, h5, h6 { font-size: 14px !important; }
  img { max-width: 100% !important; object-fit: cover; }
  img:not([height]) { height: auto !important; }
  a { color: #e066ed; word-break: break-all; overflow-wrap: anywhere; }
  pre, code { white-space: pre-wrap; word-wrap: break-word; overflow-wrap: anywhere; max-width: 100%; }
  table { border-collapse: collapse; max-width: 100% !important; table-layout: auto; }
  td, th { word-wrap: break-word; overflow-wrap: anywhere; }
  blockquote { margin: 0.8em 0; padding: 0.6em 1em; border-left: 3px solid #ddd; background: #eef1f5; max-width: 100%; }
  div, p, span, section, article { max-width: 100%; }

  .email-quote-separator {
    border: none;
    border-top: 1px solid #d0d0d0;
    margin: 16px 0 8px 0;
  }
  .email-quote-header {
    font-size: 12px;
    color: #555;
    margin: 8px 0 12px 0;
    padding: 8px 12px;
    background: #eef1f5;
    border-radius: 4px;
    border-left: 3px solid #b0b0b0;
    line-height: 1.7;
  }
  .email-quote-header strong {
    color: #333;
    font-weight: 600;
  }
  .email-quote-header .email-quote-label {
    display: inline-block;
    min-width: 60px;
    color: #777;
  }
  .email-quote-body {
    padding: 0 0 0 4px;
  }
  .gmail_quote, [class*="gmail_quote"] {
    margin: 16px 0 0 0 !important;
    padding: 12px 0 0 0 !important;
    border-top: 1px solid #d0d0d0;
  }
  #appendonsend, [id="appendonsend"],
  #divRplyFwdMsg, [id="divRplyFwdMsg"] {
    margin: 16px 0 0 0 !important;
    padding: 12px 0 0 0 !important;
    border-top: 1px solid #d0d0d0;
  }
`;
}

function normalizeAttachmentMime(mime) {
  return String(mime || "").split(";")[0].trim().toLowerCase();
}

function detectOfficeKind(attachment) {
  const mime = normalizeAttachmentMime(attachment?.contentType || attachment?.mimeType);
  const name = String(attachment?.filename || attachment?.name || "").toLowerCase();
  const isWord =
    OFFICE_WORD_TYPES.includes(mime) ||
    name.endsWith(".doc") ||
    name.endsWith(".docx");
  const isExcel =
    OFFICE_EXCEL_TYPES.includes(mime) ||
    name.endsWith(".xls") ||
    name.endsWith(".xlsx") ||
    name.endsWith(".xlsm") ||
    name.endsWith(".csv");
  const isOutlook =
    OUTLOOK_TYPES.includes(mime) ||
    name.endsWith(".eml") ||
    name.endsWith(".msg");
  return { mimeType: mime, isWord, isExcel, isOutlook };
}

function AttachmentPreviewModal({ attachment, apiBaseUrl, onClose }) {
  const { strings } = useContext(LanguageContext);
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [renderedHtml, setRenderedHtml] = useState(null);
  const [excelSheets, setExcelSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [emlData, setEmlData] = useState(null);
  const [error, setError] = useState(false);
  const { mimeType, isWord, isExcel, isOutlook } = detectOfficeKind(attachment);
  const needsConversion = isWord || isExcel || isOutlook;

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
    let revoke = null;
    const fetchPreview = async () => {
      setLoading(true);
      setError(false);
      setBlobUrl(null);
      setRenderedHtml(null);
      setExcelSheets(null);
      setEmlData(null);
      try {
        const { $user } = require("../helper/UserFactory");
        const { getFingerprintHeaders } = require("../helper/apiUtils");
        const token = $user.getToken();
        const endpoint = needsConversion ? "download" : "preview";
        // Prefer the pre-resolved URL (handles provider-only Sent/Drafts/Trash/
        // Spam attachments that have no local id); fall back to the id-based
        // route for local INBOX attachments.
        const fallbackUrl = `${apiBaseUrl}/email/attachments/${attachment.id}/${endpoint}`;
        const url = needsConversion
          ? (attachment.__downloadUrl || fallbackUrl)
          : (attachment.__previewUrl || fallbackUrl);
        const res = await fetch(url, {
          headers: {
            Authorization: `jwt ${token}`,
            ...getFingerprintHeaders(),
          },
        });
        if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
        const contentType = String(res.headers.get("content-type") || "").toLowerCase();
        if (contentType.includes("application/json")) {
          throw new Error("Preview returned JSON error");
        }

        if (isWord) {
          const buf = await res.arrayBuffer();
          const html = await convertWordToHtml(buf);
          if (!html || !String(html).trim()) {
            setError(true);
          } else {
            setRenderedHtml(html);
          }
        } else if (isExcel) {
          const buf = await res.arrayBuffer();
          let sheets;
          try {
            sheets = convertExcelToHtml(buf);
          } catch (parseErr) {
            console.error("Excel parse error:", parseErr);
            setError(true);
            setLoading(false);
            return;
          }
          if (!sheets || !sheets.length) {
            setError(true);
          } else {
            setExcelSheets(sheets);
            setActiveSheet(0);
          }
        } else if (isOutlook) {
          const text = await res.text();
          setEmlData(parseEml(text));
        } else {
          const blob = await res.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          revoke = objectUrl;
          setBlobUrl(objectUrl);
        }
      } catch (err) {
        console.error("Preview fetch error:", err);
        setError(true);
      }
      setLoading(false);
    };
    fetchPreview();
    return () => { if (revoke) window.URL.revokeObjectURL(revoke); };
  }, [attachment.id, attachment.filename, attachment.__previewUrl, attachment.__downloadUrl, apiBaseUrl, needsConversion, isWord, isExcel, isOutlook]);

  const handleDownload = async () => {
    try {
      const { $user } = require("../helper/UserFactory");
      const { getFingerprintHeaders } = require("../helper/apiUtils");
      const token = $user.getToken();
      const dlUrl = attachment.__downloadUrl || `${apiBaseUrl}/email/attachments/${attachment.id}/download`;
      const res = await fetch(dlUrl, {
        headers: {
          Authorization: `jwt ${token}`,
          ...getFingerprintHeaders(),
        },
      });
      if (!res.ok) {
        throw new Error(`Download failed (${res.status})`);
      }
      const blob = await res.blob();
      // Guard against JSON error bodies being saved as the attachment file.
      const contentType = String(res.headers.get("content-type") || "").toLowerCase();
      if (contentType.includes("application/json")) {
        throw new Error("Download returned JSON error");
      }
      triggerBlobDownload(blob, attachment.filename || "download");
    } catch (err) {
      console.error("Download failed:", err);
      $crud.notify({
        type: "error",
        message: strings.email_download_failed || strings.something_went_wrong || "Download failed",
      });
    }
  };

  const hasContent = !!(blobUrl || renderedHtml || (excelSheets && excelSheets.length) || emlData);
  const unavailableLabel = strings.email_preview_not_available || "Preview not available";

  const renderUnavailable = () => (
    <div className="attachment-preview-unsupported">
      <MaterialIcon icon="description" size={48} color="#999" />
      <p>{unavailableLabel}</p>
    </div>
  );

  const renderPreviewContent = () => {
    if (error || !hasContent) {
      return renderUnavailable();
    }

    if (mimeType.startsWith("image/") && blobUrl) {
      return <img src={blobUrl} alt={attachment.filename} className="attachment-preview-image" />;
    }
    if (mimeType === "application/pdf" && blobUrl) {
      return <iframe src={blobUrl} title={attachment.filename} className="attachment-preview-pdf" />;
    }
    if (mimeType.startsWith("text/") && blobUrl) {
      return <iframe src={blobUrl} title={attachment.filename} className="attachment-preview-text" />;
    }
    if (isWord && renderedHtml) {
      return (
        <div className="attachment-preview-document">
          <div className="attachment-preview-doc-content" dangerouslySetInnerHTML={{ __html: renderedHtml }} />
        </div>
      );
    }
    if (isExcel && excelSheets && excelSheets.length) {
      return (
        <div className="attachment-preview-document">
          {excelSheets.length > 1 && (
            <div className="attachment-preview-sheet-tabs">
              {excelSheets.map((s, i) => (
                <button
                  key={i}
                  className={`kj-tab${i === activeSheet ? " is-active" : ""}`}
                  onClick={() => setActiveSheet(i)}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <div className="attachment-preview-doc-content attachment-preview-excel" dangerouslySetInnerHTML={{ __html: excelSheets[activeSheet]?.html || "" }} />
        </div>
      );
    }
    if (isOutlook && emlData) {
      return (
        <div className="attachment-preview-document">
          <div className="attachment-preview-doc-content attachment-preview-eml">
            <div className="eml-header">
              {emlData.subject && <h3 className="email-detail-view__eml-subject">{emlData.subject}</h3>}
              {emlData.from && <div><strong>From:</strong> {emlData.from}</div>}
              {emlData.to && <div><strong>To:</strong> {emlData.to}</div>}
              {emlData.date && <div><strong>Date:</strong> {emlData.date}</div>}
            </div>
            <hr className="email-detail-view__eml-rule" />
            <pre className="email-detail-view__eml-body">{emlData.body}</pre>
          </div>
        </div>
      );
    }
    return renderUnavailable();
  };

  return ReactDOM.createPortal(
    <div className="attachment-preview-overlay" onClick={onClose}>
      <div className="attachment-preview-container" onClick={(e) => e.stopPropagation()}>
        <div className="attachment-preview-header">
          <span className="attachment-preview-filename">{attachment.filename}</span>
          <div className="attachment-preview-actions">
            <button className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral" onClick={handleDownload} title={strings.email_download || "Download"}>
              <MaterialIcon icon="download" size={20} />
            </button>
            <button className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral" onClick={onClose} title={strings.email_close || "Close"}>
              <MaterialIcon icon="close" size={20} />
            </button>
          </div>
        </div>
        <div className="attachment-preview-body">
          {loading && (
            <div className="attachment-preview-loading">
              <MaterialIcon icon="autorenew" size={40} color="#555" className="email-refresh-spin" />
              <span className="attachment-preview-loading-text">{strings.email_loading_preview || "Loading preview..."}</span>
            </div>
          )}
          <div className={`attachment-preview-content ${!loading ? "visible" : ""}`}>
            {!loading && renderPreviewContent()}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + " " + sizes[i];
}

const InlineReplyCompose = forwardRef(function InlineReplyCompose({ email, mode, accountId: rawAccountId, emailAccounts, onSent, onCancel, buildings = [], initialBuildingId = null }, ref) {
  const { strings, language } = useContext(LanguageContext);
  const locale = LOCALE_MAP[language?.toLowerCase()] || LOCALE_MAP.en;
  const toRef = useRef(null);
  const fromRef = useRef(null);
  const originalIframeRef = useRef(null);
  const originalQuoteHtmlRef = useRef("");
  const writtenQuoteRef = useRef(null);
  const richBodyRef = useRef(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const initialBodyRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});
  const accounts = emailAccounts || [];

  const initialLinked = (() => {
    if (initialBuildingId) {
      const fromList = (buildings || []).find((b) => String(b.id) === String(initialBuildingId));
      if (fromList) return { id: fromList.id, name: fromList.name || "" };
      return { id: initialBuildingId, name: "" };
    }
    if (Array.isArray(email?.linkedProjects) && email.linkedProjects.length > 0) {
      return email.linkedProjects[0];
    }
    return null;
  })();
  const [projectId, setProjectId] = useState(initialLinked?.id || null);
  const [projectName, setProjectName] = useState(initialLinked?.name || "");
  const [showProjectInfo, setShowProjectInfo] = useState(false);
  const composeProjectRef = useRef(null);
  const [roomOptions, setRoomOptions] = useState(buildings || []);

  useEffect(() => {
    setRoomOptions(buildings || []);
  }, [buildings]);

  useEffect(() => {
    if (!initialBuildingId || projectName) return;
    const fromList = (roomOptions || []).find((b) => String(b.id) === String(initialBuildingId));
    if (fromList) {
      setProjectId(fromList.id);
      setProjectName(fromList.name || "");
    }
  }, [initialBuildingId, roomOptions, projectName]);

  useEffect(() => {
    if (buildings && buildings.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await callAPI(`/email/classification/buildings`, {}, "GET");
        const data = res?.data || res;
        const list = data?.buildings && Array.isArray(data.buildings) ? data.buildings : [];
        if (!cancelled) {
          setRoomOptions(list.map((b) => ({
            id: b.id,
            name: b.name,
            themeColor: b.themeColor || null,
            projectNo: b.projectNo || "",
            aliases: Array.isArray(b.aliases) ? b.aliases : [],
          })));
        }
      } catch (_) { /* picker just won't have suggestions */ }
    })();
    return () => { cancelled = true; };
  }, [buildings]);

  const resolveAccountId = (id) => {
    if (id && accounts.find((a) => String(a.id || a._id) === String(id))) return id;
    return accounts.length > 0 ? (accounts[0].id || accounts[0]._id) : id;
  };
  const accountId = resolveAccountId(
    mode === "draft" ? pickComposeAccountId(email, accounts, rawAccountId) : rawAccountId
  );

  const updateActiveFormats = useCallback(() => {
    try {
      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikethrough: document.queryCommandState("strikeThrough"),
        unordered: document.queryCommandState("insertUnorderedList"),
        ordered: document.queryCommandState("insertOrderedList"),
      });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", updateActiveFormats);
    return () => document.removeEventListener("selectionchange", updateActiveFormats);
  }, [updateActiveFormats]);

  const applyFormat = useCallback((formatType) => {
    const editor = richBodyRef.current;
    if (!editor) return;
    editor.focus();
    switch (formatType) {
      case "bold": document.execCommand("bold", false, null); break;
      case "italic": document.execCommand("italic", false, null); break;
      case "underline": document.execCommand("underline", false, null); break;
      case "strikethrough": document.execCommand("strikeThrough", false, null); break;
      case "unordered": document.execCommand("insertUnorderedList", false, null); break;
      case "ordered": document.execCommand("insertOrderedList", false, null); break;
      case "link": {
        const url = window.prompt(strings.enter_url || "Enter URL");
        if (!url) break;
        const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        document.execCommand("createLink", false, href);
        const sel = window.getSelection();
        const anchor = sel?.anchorNode?.parentElement?.closest?.("a") || editor.querySelector("a[href]:last-of-type");
        if (anchor) {
          anchor.setAttribute("target", "_blank");
          anchor.setAttribute("rel", "noopener noreferrer");
        }
        break;
      }
      default: break;
    }
    setTimeout(updateActiveFormats, 0);
  }, [updateActiveFormats, strings.enter_url]);

  const getRichBodyHtml = useCallback(() => {
    const editor = richBodyRef.current;
    if (!editor) return "";
    linkifyElement(editor);
    return editor.innerHTML;
  }, []);

  const sourceDraftIds = (draft) => {
    if (!draft) return {};
    const draftId = draft.draftId || undefined;
    const draftProviderMessageId = draft.providerMessageId || draft.id || draft._id || undefined;
    if (!draftId && !draftProviderMessageId) return {};
    if (draftId && draftProviderMessageId && String(draftId) === String(draftProviderMessageId)) {
      return { draftProviderMessageId };
    }
    return { draftId, draftProviderMessageId };
  };

  const attachSourceDraft = (payload, draft, asFormData = false) => {
    const ids = sourceDraftIds(draft);
    if (!ids.draftId && !ids.draftProviderMessageId) return;
    if (asFormData) {
      if (ids.draftId) payload.append("draftId", ids.draftId);
      if (ids.draftProviderMessageId) payload.append("draftProviderMessageId", ids.draftProviderMessageId);
    } else {
      if (ids.draftId) payload.draftId = ids.draftId;
      if (ids.draftProviderMessageId) payload.draftProviderMessageId = ids.draftProviderMessageId;
    }
  };

  const deleteDraftOnServer = async (accId, draft) => {
    if (!draft) return;
    const ids = sourceDraftIds(draft);
    if (!ids.draftId && !ids.draftProviderMessageId) return;
    try {
      await callAPI(`/email/accounts/${accId}/drafts/delete`, {}, "POST", {
        draftId: ids.draftId,
        providerMessageId: ids.draftProviderMessageId,
      });
    } catch (e) {
      console.warn("Draft cleanup failed (non-fatal):", e);
    }
  };

  const buildInitialState = () => {
    if (mode === "draft" && email) {
      return {
        to: emailsFromRecipientField(email.toAddresses, email.to).join(", "),
        cc: emailsFromRecipientField(email.ccAddresses, email.cc).join(", "),
        bcc: emailsFromRecipientField(email.bccAddresses, email.bcc).join(", "),
        subject: email.subject || "",
        body: email.bodyHtml || email.html || email.bodyText || email.snippet || "",
      };
    }
    if (mode === "reply" && email) {
      const replyTo = email.fromAddress?.email || email.from || "";
      const subj = email.subject || "";
      const prefix = subj.toLowerCase().startsWith("re:") ? "" : "Re: ";
      return {
        to: replyTo,
        cc: "",
        bcc: "",
        subject: `${prefix}${subj}`,
        body: "",
      };
    }
    if (mode === "replyAll" && email) {
      const senderEmail = (email.fromAddress?.email || email.from || "").toLowerCase();
      const myAccounts = accounts.map((a) => (a.email || "").toLowerCase()).filter(Boolean);

      const extractEmails = (addresses, fallback) => {
        if (Array.isArray(addresses) && addresses.length > 0) {
          return addresses
            .map((a) => (typeof a === "string" ? a.trim() : (a?.email || "").trim()))
            .filter(Boolean);
        }
        if (typeof fallback === "string" && fallback.trim()) {
          return fallback.split(",").map((s) => s.trim()).filter(Boolean);
        }
        return [];
      };

      const originalTo = extractEmails(email.toAddresses, email.to);
      const originalCc = extractEmails(email.ccAddresses, email.cc);

      const isMe = (addr) => myAccounts.includes(addr.toLowerCase());

      const toList = [senderEmail, ...originalTo.filter((a) => !isMe(a) && a.toLowerCase() !== senderEmail)];
      const ccList = originalCc.filter((a) => !isMe(a) && !toList.some((t) => t.toLowerCase() === a.toLowerCase()));

      const subj = email.subject || "";
      const prefix = subj.toLowerCase().startsWith("re:") ? "" : "Re: ";
      return {
        to: toList.join(", "),
        cc: ccList.join(", "),
        bcc: "",
        subject: `${prefix}${subj}`,
        body: "",
      };
    }
    if (mode === "forward" && email) {
      const subj = email.subject || "";
      const prefix = subj.toLowerCase().startsWith("fwd:") ? "" : "Fwd: ";
      return {
        to: "",
        cc: "",
        bcc: "",
        subject: `${prefix}${subj}`,
        body: "",
      };
    }
    return { to: "", cc: "", bcc: "", subject: "", body: "" };
  };

  const [form, setForm] = useState(() => {
    const init = buildInitialState();
    initialBodyRef.current = init.body;
    return init;
  });
  const [signature, setSignature] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(
    () =>
      (mode === "replyAll" && !!buildInitialState().cc)
      || (mode === "draft" && !!(
        emailsFromRecipientField(email?.ccAddresses, email?.cc).length
        || emailsFromRecipientField(email?.bccAddresses, email?.bcc).length
      ))
  );
  const [sending, setSending] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(accountId);

  useEffect(() => {
    const stillValid = selectedAccountId
      && accounts.some((a) => String(a.id || a._id) === String(selectedAccountId));
    if (stillValid) return;
    if (accountId) setSelectedAccountId(accountId);
  }, [accountId, accounts.length]);
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [originalAttachments, setOriginalAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (mode === "draft") {
      setOriginalAttachments(draftAttachmentsOf(email));
    } else if ((mode === "reply" || mode === "replyAll" || mode === "forward") && Array.isArray(email?.attachments) && email.attachments.length > 0) {
      const carried = email.attachments
        .filter((att) => att && att.id)
        .map((att) => ({
          id: att.id,
          filename: att.filename || "attachment",
          size: Number(att.size) || 0,
          mimeType: att.mimeType || att.contentType || "application/octet-stream",
        }));
      setOriginalAttachments(carried);
    } else {
      setOriginalAttachments([]);
    }
  }, [email?.id, email?._id, mode]);

  const handleRemoveOriginalAttachment = (index) => {
    setOriginalAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const selectedAccount = findAccountById(accounts, selectedAccountId);
  const selectedEmailAddr = selectedAccount?.email || selectedAccount?.name || selectedAccount?.provider || "";
  const accountSignature = signatureForAccount(accounts, selectedAccountId);
  const signatureToShow = accountSignature != null ? accountSignature : signature;

  const fetchSignature = async (accId) => {
    const known = signatureForAccount(accounts, accId);
    if (known != null) {
      setSignature(known);
      return;
    }
    try {
      const { data } = await callAPI("/email/accounts", {}, "GET");
      const accts = data?.accounts || (Array.isArray(data) ? data : []);
      const acc = findAccountById(accts, accId);
      setSignature(acc?.emailSignature || "");
    } catch (e) {
      console.warn("Failed to fetch signature:", e);
      setSignature("");
    }
  };

  const buildOutgoingBody = (sourceEmail = email) => {
    const html = getRichBodyHtml();
    const userContent = html || form.body;

    if ((mode === "reply" || mode === "replyAll" || mode === "forward") && sourceEmail) {
      const iframeDoc = originalIframeRef.current && (originalIframeRef.current.contentDocument || originalIframeRef.current.contentWindow?.document);
      const iframeHtml = iframeDoc && iframeDoc.body ? iframeDoc.body.innerHTML : "";
      const fromEmail = resolveEmailBody(sourceEmail);
      const snapshot = originalQuoteHtmlRef.current || "";
      const rawBody = [snapshot, iframeHtml, fromEmail].reduce(
        (best, candidate) => preferLongerText(best, candidate),
        ""
      );
      const isOrigHtml = looksLikeHtml(rawBody);
      const { body: origBody } = isOrigHtml ? prepareEmailHtml(rawBody) : { body: rawBody };

      const fromLine = getSenderDisplay(sourceEmail);
      const dateLine = formatOriginalDate(sourceEmail.date || sourceEmail.receivedAt);
      const toLine = formatRecipientList(sourceEmail.toAddresses, sourceEmail.to);
      const ccLine = formatRecipientList(sourceEmail.ccAddresses, sourceEmail.cc);
      const subjectLine = sourceEmail.subject || "";

      let headerHtml = `<div style="font-size:12px;color:#555;margin:8px 0;padding:8px 12px;background:#f5f5f5;border-left:3px solid #b0b0b0;line-height:1.7;">`;
      headerHtml += `<b>From:</b> ${fromLine}<br>`;
      headerHtml += `<b>Sent:</b> ${dateLine}<br>`;
      if (toLine) headerHtml += `<b>To:</b> ${toLine}<br>`;
      if (ccLine) headerHtml += `<b>Cc:</b> ${ccLine}<br>`;
      headerHtml += `<b>Subject:</b> ${subjectLine}`;
      headerHtml += `</div>`;

      const originalContent = isOrigHtml
        ? origBody
        : `<pre style="white-space:pre-wrap;font-family:inherit;">${linkifyPlainText(origBody)}</pre>`;
      return `${userContent}<br><hr style="border:none;border-top:1px solid #ccc;margin:16px 0;">${headerHtml}<div style="padding:0 4px;">${originalContent}</div>`;
    }

    return userContent;
  };

  useEffect(() => {
    fetchSignature(selectedAccountId);
  }, [selectedAccountId, accounts.length]);

  const fromChangedRef = useRef(false);
  const draftBodyHydratedRef = useRef(false);

  useEffect(() => {
    const toApply = signatureToShow || "";
    const apply = () => {
      const editor = richBodyRef.current;
      if (!editor) return false;
      if (form.body && (editorLooksEmpty(editor) || (mode === "draft" && !draftBodyHydratedRef.current))) {
        hydrateComposeEditorBody(editor, form.body);
        draftBodyHydratedRef.current = true;
      }
      const hasSigBlock = !!editor.querySelector(".email-signature-block");
      if (mode === "draft" && !fromChangedRef.current && hasSigBlock) {
        return true;
      }
      applyEmailSignatureToEditor(editor, toApply);
      const html = editor.innerHTML || "";
      setForm((f) => (f.body === html ? f : { ...f, body: html }));
      return true;
    };
    if (!apply()) {
      const timers = [];
      timers.push(setTimeout(() => {
        if (!apply()) timers.push(setTimeout(apply, 300));
      }, 50));
      return () => timers.forEach(clearTimeout);
    }
    return undefined;
  }, [selectedAccountId, signatureToShow, mode]);

  useEffect(() => {
    if (mode !== "draft" || !email) return;
    const nextTo = emailsFromRecipientField(email.toAddresses, email.to).join(", ");
    const nextCc = emailsFromRecipientField(email.ccAddresses, email.cc).join(", ");
    const nextBcc = emailsFromRecipientField(email.bccAddresses, email.bcc).join(", ");
    if (nextCc || nextBcc) setShowCcBcc(true);
    setForm((f) => {
      const to = f.to || nextTo;
      const cc = f.cc || nextCc;
      const bcc = f.bcc || nextBcc;
      if (to === f.to && cc === f.cc && bcc === f.bcc) return f;
      return { ...f, to, cc, bcc };
    });
  }, [mode, email]);

  useEffect(() => {
    if (mode !== "draft" || !email) return;
    const nextBody = email.bodyHtml || email.html || email.bodyText || "";
    if (!nextBody) return;
    const incoming = String(nextBody).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
    setForm((f) => {
      const current = String(f.body || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
      if (incoming.length <= current.length + 10) return f;
      draftBodyHydratedRef.current = false;
      return {
        ...f,
        to: f.to || emailsFromRecipientField(email.toAddresses, email.to).join(", "),
        cc: f.cc || emailsFromRecipientField(email.ccAddresses, email.cc).join(", "),
        bcc: f.bcc || emailsFromRecipientField(email.bccAddresses, email.bcc).join(", "),
        subject: f.subject || email.subject || "",
        body: nextBody,
      };
    });
  }, [mode, email?.bodyHtml, email?.html, email?.bodyText]);

  useEffect(() => {
    if (mode === "forward" || mode === "new" || mode === "draft") {
      setTimeout(() => toRef.current?.focus(), 100);
    }
  }, [mode]);

  useEffect(() => {
    if (!showFromDropdown) return;
    const handleClickOutside = (e) => {
      if (fromRef.current && !fromRef.current.contains(e.target)) {
        setShowFromDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFromDropdown]);

  useEffect(() => {
    if (mode === "new" || mode === "draft") return undefined;
    const iframe = originalIframeRef.current;
    if (iframe && email) {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        const rawBody = resolveEmailBody(email);
        if (strippedBodyLen(rawBody) > strippedBodyLen(originalQuoteHtmlRef.current)) {
          originalQuoteHtmlRef.current = rawBody;
        }
        const isHtml = looksLikeHtml(rawBody);
        const { styles: emailStyles, body: emailBody } = isHtml
          ? prepareEmailHtml(rawBody)
          : { styles: '', body: rawBody };
        const html = buildEmailIframeDocument({
          styles: emailStyles,
          body: isHtml ? emailBody : `<pre style="white-space:pre-wrap;font-family:inherit;">${linkifyPlainText(emailBody)}</pre>`,
          fontSize: "var(--kjuup-type-body)",
          bodyPadding: "8px 0",
        });
        // See the detail-pane effect below: rewriting costs the reader their
        // selection, so skip it when the quoted mail is unchanged.
        if (writtenQuoteRef.current !== html || !doc.body || !doc.body.firstChild) {
          doc.open();
          doc.write(html);
          doc.close();
          writtenQuoteRef.current = html;
          cleanupEmailImages(doc);
          makeLinksOpenNewTab(doc);
          formatEmailConversationHistory(doc);
        }
        return combineCleanups(
          resizeIframeToContent(iframe),
          attachIframeScrollRelay(iframe),
          attachEmailChatLinkHandler(doc)
        );
      }
    }
    return undefined;
  }, [email]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleRecipientKeyDown = (field) => (e) => {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Tab") return;
    const current = form[field] || "";
    const lastSeg = current.split(",").pop().trim();
    if (!lastSeg) return;
    const isEmail = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/.test(lastSeg);
    if (!isEmail) return;
    if (e.key === "Enter") e.preventDefault();
    if (e.key === " ") e.preventDefault();
    setForm((f) => {
      const v = (f[field] || "").trimEnd();
      const ends = v.endsWith(",");
      return { ...f, [field]: ends ? v + " " : v + ", " };
    });
  };

  const handleSelectAccount = (id) => {
    fromChangedRef.current = true;
    setSelectedAccountId(id);
    setShowFromDropdown(false);
  };

  const formatOriginalDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSenderDisplay = (source = email) => formatEmailSenderDisplay(source);

  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setAttachments((prev) => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && file.type.startsWith('image/')) {
          const ts = new Date().toISOString().replace(/[:.]/g, '-');
          const ext = (file.type.split('/')[1] || 'png').split('+')[0];
          const named = new File([file], file.name && file.name !== 'image.png' ? file.name : `screenshot-${ts}.${ext}`, { type: file.type });
          pastedFiles.push(named);
        }
      }
    }
    if (pastedFiles.length) {
      e.preventDefault();
      setAttachments((prev) => [...prev, ...pastedFiles]);
    }
  };

  const handleComposeDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes("Files")) setIsDragOver(true);
  };
  const handleComposeDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };
  const handleComposeDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleComposeDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) setAttachments((prev) => [...prev, ...files]);
  };

  const handleSend = async () => {
    if (!form.to.trim()) {
      await $crud.notify({
        type: "error",
        message: strings.email_compose_to_required || "Recipient (To) is required.",
      });
      return;
    }
    const richHtml = getRichBodyHtml();
    const hasBody = richHtml ? richHtml.replace(/<[^>]*>/g, '').trim().length > 0 : form.body.trim().length > 0;
    if ((mode === "new" || mode === "draft") && !hasBody && !form.subject.trim()) {
      await $crud.notify({
        type: "error",
        message: strings.email_compose_body_required || "Please enter a subject or message body.",
      });
      return;
    }

    let assignedProjectId = projectId || null;
    if (composeProjectRef.current && composeProjectRef.current.ensureAssigned) {
      const ensured = await composeProjectRef.current.ensureAssigned();
      if (ensured === false) return;
      if (ensured && ensured.id) assignedProjectId = ensured.id;
    }

    setSending(true);
    try {
      const action = (mode === "reply" || mode === "replyAll") ? "reply" : mode === "forward" ? "forward" : "send";
      let sourceEmail = email;
      if ((mode === "reply" || mode === "replyAll" || mode === "forward") && email) {
        const messageId = email.id || email._id;
        if (isPersistedEmailId(messageId) && !hasFullBody(email)) {
          try {
            const accountId = getEmailAccountId(email) || selectedAccountId;
            const { data } = await callAPI(
              `/email/messages/${encodeURIComponent(String(messageId))}`,
              accountId ? { accountId } : {},
              "GET"
            );
            const full = data && (data.message || data.data || data);
            if (full && (full.bodyHtml || full.bodyText || full.html)) {
              sourceEmail = mergeEnrichedEmail(email, full);
              const hydrated = resolveEmailBody(sourceEmail);
              if (strippedBodyLen(hydrated) > strippedBodyLen(originalQuoteHtmlRef.current)) {
                originalQuoteHtmlRef.current = hydrated;
              }
            }
          } catch (hydrateErr) {
            console.warn("Failed to hydrate original email before send:", hydrateErr);
          }
        }
      }
      const outgoingBody = buildOutgoingBody(sourceEmail);
      const originalAttachmentIds = (mode === "reply" || mode === "replyAll" || mode === "forward")
        ? originalAttachments.map((a) => a.id).filter(Boolean)
        : [];
      // Reply/forward from a room may show another member's email. Send from
      // that mailbox only when it is one of the current user's accounts.
      const actionAccountId = (mode === "reply" || mode === "replyAll" || mode === "forward") && email
        ? pickOwnedEmailAccountId(email, accounts, selectedAccountId)
        : selectedAccountId;
      let result;

      let resolvedBuildingId = assignedProjectId || projectId || null;
      if (!resolvedBuildingId && projectName && String(projectName).trim()) {
        const exact = findExactProject(projectName, roomOptions);
        if (exact) resolvedBuildingId = exact.id;
      }

      if (attachments.length > 0) {
        const formData = new FormData();
        formData.append("action", action);
        formData.append("to", form.to.trim());
        formData.append("cc", form.cc.trim());
        formData.append("bcc", form.bcc.trim());
        formData.append("subject", form.subject);
        const isHtmlBody = looksLikeHtml(outgoingBody);
        formData.append("text", isHtmlBody ? outgoingBody.replace(/<[^>]*>/g, '') : outgoingBody);
        formData.append("html", isHtmlBody ? outgoingBody : "");
        formData.append("replyTo", "");
        formData.append("buildingId", resolvedBuildingId || "");
        if ((mode === "reply" || mode === "replyAll" || mode === "forward") && email) {
          formData.append("messageId", email.id || email._id);
        }
        if (originalAttachmentIds.length > 0) {
          formData.append("originalAttachmentIds", JSON.stringify(originalAttachmentIds));
        }
        if (mode === "draft" && email) {
          attachSourceDraft(formData, email, true);
          formData.append("draftAttachments", JSON.stringify(draftAttachmentsField(originalAttachments)));
        }
        attachments.forEach((file) => formData.append("attachments", file));

        const token = require("../helper/UserFactory").$user.getToken();
        const res = await fetch(
          `${API_ROOT}/email/accounts/${actionAccountId}/action`,
          { method: "POST", headers: { Authorization: `jwt ${token}` }, body: formData }
        );
        const raw = await res.text();
        try {
          result = raw ? JSON.parse(raw) : {};
        } catch (_) {
          // Provider may have sent successfully while the response body was
          // truncated/non-JSON (large reply + attachments). Prefer success on HTTP OK.
          result = res.ok
            ? { status: true, message: strings.email_sent || "Email sent." }
            : { status: false, message: strings.email_send_failed || "Failed to send email." };
        }
        if (res.ok && result?.status !== false) {
          result = { ...result, status: true };
        }
      } else {
        const isHtmlBody = looksLikeHtml(outgoingBody);
        const payload = {
          action,
          to: form.to.trim(),
          cc: form.cc.trim(),
          bcc: form.bcc.trim(),
          subject: form.subject,
          text: isHtmlBody ? outgoingBody.replace(/<[^>]*>/g, '') : outgoingBody,
          html: isHtmlBody ? outgoingBody : "",
          replyTo: "",
          buildingId: resolvedBuildingId || "",
        };
        if ((mode === "reply" || mode === "replyAll" || mode === "forward") && email) {
          payload.messageId = email.id || email._id;
        }
        if (originalAttachmentIds.length > 0) {
          payload.originalAttachmentIds = originalAttachmentIds;
        }
        if (mode === "draft" && email) {
          attachSourceDraft(payload, email, false);
          payload.draftAttachments = draftAttachmentsField(originalAttachments);
        }
        const { data } = await callAPI(
          `/email/accounts/${actionAccountId}/action`,
          {},
          "POST",
          payload
        );
        result = data;
      }

      // $crud.notify() only resolves when the snackbar is dismissed — never
      // await it before clearing the sending overlay / closing compose.
      if (result?.status === false) {
        $crud.notify({
          type: "error",
          message: result?.message || strings.email_send_failed || "Failed to send email.",
        });
      } else {
        if (mode === "draft" && email) {
          try {
            await deleteDraftOnServer(
              pickComposeAccountId(email, accounts, selectedAccountId),
              email
            );
          } catch (draftErr) {
            console.warn("Draft cleanup after send failed:", draftErr);
          }
        }
        $crud.notify({
          type: "success",
          message: result?.message || strings.email_sent || "Email sent.",
        });
        try {
          if (onSent) onSent(mode === "draft" ? { sentDraft: true } : undefined);
        } catch (sideEffectErr) {
          console.warn("Email onSent side-effect failed:", sideEffectErr);
        }
        try {
          onCancel();
        } catch (cancelErr) {
          console.warn("Email onCancel side-effect failed:", cancelErr);
        }
      }
    } catch (err) {
      console.error("Send email error:", err);
      $crud.notify({
        type: "error",
        message: strings.email_send_failed || "Failed to send email.",
      });
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    let saved = false;
    try {
      const outgoingBody = buildOutgoingBody();
      const isHtmlBody = looksLikeHtml(outgoingBody);
      const payload = {
        to: form.to.trim(),
        cc: form.cc.trim(),
        bcc: form.bcc.trim(),
        subject: form.subject,
        text: isHtmlBody ? outgoingBody.replace(/<[^>]*>/g, " ") : outgoingBody,
        html: isHtmlBody ? outgoingBody : "",
        buildingId: projectId || "",
      };
      if (email?.threadId) payload.threadId = email.threadId;
      if (mode === "draft" && email) {
        attachSourceDraft(payload, email, false);
        payload.draftAttachments = draftAttachmentsField(originalAttachments);
      } else if (email && originalAttachments.length > 0) {
        payload.messageId = email.id || email._id;
        payload.originalAttachmentIds = originalAttachments.map((a) => a.id).filter(Boolean);
      }
      const { status, data } = await callAPI(
        `/email/accounts/${selectedAccountId}/draft`,
        {},
        "POST",
        draftRequestBody(payload, attachments)
      );
      const requestFailed = status < 200 || status >= 300;
      if (requestFailed || data?.status === false) {
        await $crud.notify({ type: "error", message: data?.message || strings.email_draft_save_failed || "Failed to save draft." });
      } else {
        saved = true;
        if (onSent) onSent({ savedDraft: true });
        $crud.notify({ type: "success", message: strings.email_draft_saved || "Draft saved." });
      }
    } catch (err) {
      console.error("Save draft error:", err);
      await $crud.notify({ type: "error", message: strings.email_draft_save_failed || "Failed to save draft." });
    }
    setSavingDraft(false);
    return saved;
  };

  const handleCancel = async () => {
    if (sending || savingDraft) return false;
    const currentBody = composePlainWithoutSignature(getRichBodyHtml() || form.body);
    const initialBody = composePlainWithoutSignature(initialBodyRef.current || "");
    const bodyChanged = currentBody !== initialBody;
    const hasContent = form.to.trim() || form.cc.trim() || form.bcc.trim() || form.subject.trim() || bodyChanged || attachments.length > 0;
    if (!hasContent) {
      onCancel();
      return true;
    }
    const choice = await promptSaveDraft(strings);
    if (choice === "stay") return false;
    if (choice === "save") {
      const saved = await handleSaveDraft();
      if (!saved) return false;
    }
    onCancel();
    return true;
  };

  const handleCancelRef = useRef(handleCancel);
  handleCancelRef.current = handleCancel;

  useImperativeHandle(ref, () => ({
    requestClose: async () => handleCancelRef.current(),
    focusEditor: () => {
      const editor = richBodyRef.current;
      if (!editor) return;
      editor.focus();
    },
  }), []);

  return (
    <div
      className="email-inline-reply"
      onDragEnter={handleComposeDragEnter}
      onDragLeave={handleComposeDragLeave}
      onDragOver={handleComposeDragOver}
      onDrop={handleComposeDrop}
      onPaste={handlePaste}
    >
      {/* Toolbar: Send + From + Discard */}
      <div className="email-inline-reply-toolbar">
        <div className="email-detail-view__toolbar-start">
          <button
            type="button"
            className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--round email-compose-back-btn"
            onClick={handleCancel}
            title={strings.email_back || strings.back || "Back"}
          >
            <MaterialIcon icon="arrow_back" size={20} />
          </button>
          <button
            className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover email-compose-send-btn"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? (
              <MaterialIcon icon="autorenew" size={16} className="email-refresh-spin" />
            ) : (
              <>
                <MaterialIcon icon="send" size={14} className="email-detail-view__send-icon" />
                {strings.email_send_btn || "Send"}
              </>
            )}
          </button>

          <div ref={fromRef} className="position-relative">
            <div
              className={`email-compose-from-selector email-detail-view__from${accounts.length > 1 ? " email-detail-view__from--multi" : ""}`}
              onClick={() => accounts.length > 1 && setShowFromDropdown((v) => !v)}
            >
              <span className="email-detail-view__from-label">
                {strings.email_from || "From"}:
              </span>
              <span className="kj-fw-500">
                {selectedEmailAddr}
              </span>
              {accounts.length > 1 && (
                <MaterialIcon icon="expand_more" size={14} color="#666" className="email-detail-view__from-caret" />
              )}
            </div>
            {showFromDropdown && accounts.length > 1 && (
              <div className="email-compose-from-dropdown">
                {accounts.map((acc) => {
                  const id = acc.id || acc._id;
                  const accEmail = acc.email || acc.name || acc.provider;
                  const isSelected = String(id) === String(selectedAccountId);
                  return (
                    <div
                      key={id}
                      className={`email-compose-from-option ${isSelected ? "active" : ""}`}
                      onClick={() => handleSelectAccount(id)}
                    >
                      {isSelected && (
                        <span className="email-compose-from-checkmark">✓</span>
                      )}
                      <span>{accEmail}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="email-detail-view__toolbar-end">
          <button
            className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--danger"
            onClick={handleCancel}
            disabled={savingDraft}
            title={strings.cancel || "Discard"}
          >
            <MaterialIcon icon="delete" size={18} />
          </button>
        </div>
      </div>

      <div className="email-inline-reply-scroll">
      {/* Compose Fields */}
      <div className="email-inline-reply-fields">
        <div className="email-compose-field-row">
          <label className="email-compose-label">{strings.email_to || "To"}</label>
          <EmailRecipientInput
            inputRef={toRef}
            value={form.to}
            onChange={(v) => setForm((f) => ({ ...f, to: v }))}
            onKeyDown={handleRecipientKeyDown("to")}
            placeholder="recipient@example.com"
            disabled={sending}
            accountId={selectedAccountId}
            ariaLabel={strings.email_to || "To"}
          />
          <button
            className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--email kj-btn--sm"
            onClick={() => setShowCcBcc((v) => !v)}
            title="Cc / Bcc"
            type="button"
          >
            {showCcBcc ? <MaterialIcon icon="expand_less" size={16} /> : <MaterialIcon icon="expand_more" size={16} />}
            <span className="email-detail-view__ccbcc-label">Cc/Bcc</span>
          </button>
        </div>
        {showCcBcc && (
          <>
            <div className="email-compose-field-row">
              <label className="email-compose-label">Cc</label>
              <EmailRecipientInput
                value={form.cc}
                onChange={(v) => setForm((f) => ({ ...f, cc: v }))}
                onKeyDown={handleRecipientKeyDown("cc")}
                placeholder="cc@example.com"
                disabled={sending}
                accountId={selectedAccountId}
                ariaLabel="Cc"
              />
            </div>
            <div className="email-compose-field-row">
              <label className="email-compose-label">Bcc</label>
              <EmailRecipientInput
                value={form.bcc}
                onChange={(v) => setForm((f) => ({ ...f, bcc: v }))}
                onKeyDown={handleRecipientKeyDown("bcc")}
                placeholder="bcc@example.com"
                disabled={sending}
                accountId={selectedAccountId}
                ariaLabel="Bcc"
              />
            </div>
          </>
        )}
        <div className="email-compose-field-row">
          <label className="email-compose-label">{strings.email_subject || "Subject"}</label>
          <input
            type="text"
            className="email-compose-input"
            value={form.subject}
            onChange={handleChange("subject")}
            disabled={sending}
          />
        </div>
        <div className="email-compose-field-row email-compose-field-row--project">
          <span className="email-compose-label email-compose-project-label">
            <span>{strings.email_compose_project || "Project association in Kjuup"}</span>
            <button
              type="button"
              className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email"
              title={strings.info || "Info"}
              onClick={() => setShowProjectInfo((v) => !v)}
              aria-expanded={showProjectInfo}
            >
              <MaterialIcon icon="info" size={16} color="#b15ec0" />
            </button>
          </span>
          <ComposeProjectField
            ref={composeProjectRef}
            name={projectName}
            projectId={projectId}
            buildings={roomOptions}
            disabled={sending}
            strings={strings}
            onNameChange={(v) => {
              setProjectName(v);
              const exact = findExactProject(v, roomOptions);
              setProjectId(exact ? exact.id : null);
            }}
            onAssigned={({ id, name }) => {
              setProjectId(id || null);
              setProjectName(name || "");
              if (id) {
                setRoomOptions((prev) => {
                  if ((prev || []).some((b) => String(b.id) === String(id))) return prev;
                  return [{ id, name: name || "" }, ...(prev || [])];
                });
              }
            }}
          />
        </div>
        {showProjectInfo && (
          <div className="email-compose-project-info">
            {strings.email_compose_project_info ||
              "This information is only visible to you and will not be sent with the email."}
          </div>
        )}
      </div>

      {/* Attachments */}
      <div className="email-compose-attachments-bar email-detail-view__attachments-bar">
        <button
          type="button"
          className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--sm kj-btn--pill"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
          title={strings.email_attach || "Attach files"}
        >
          <MaterialIcon icon="attach_file" size={16} />
          <span>{strings.email_attach || "Attach"}</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleAddFiles}
          className="d-none"
        />
      </div>

      {(attachments.length > 0 || originalAttachments.length > 0) && (
        <div className="email-compose-attachment-list email-detail-view__attachment-list">
          {originalAttachments.map((att, i) => (
            <div
              key={`orig-${att.id}`}
              className="email-compose-attachment-chip"
              title={strings.email_attachment_from_original || "From original message"}
            >
              <MaterialIcon icon="attach_file" size={13} />
              <span className="email-compose-attachment-name">{att.filename}</span>
              {att.size > 0 && (
                <span className="email-compose-attachment-size">({formatFileSize(att.size)})</span>
              )}
              <button
                type="button"
                className="email-compose-attachment-remove"
                onClick={() => handleRemoveOriginalAttachment(i)}
                title={strings.email_remove || "Remove"}
              >
                <MaterialIcon icon="close" size={12} />
              </button>
            </div>
          ))}
          {attachments.map((file, i) => (
            <div key={`${file.name}-${i}`} className="email-compose-attachment-chip">
              <MaterialIcon icon="attach_file" size={13} />
              <span className="email-compose-attachment-name">{file.name}</span>
              <span className="email-compose-attachment-size">({formatFileSize(file.size)})</span>
              <button
                type="button"
                className="email-compose-attachment-remove"
                onClick={() => handleRemoveAttachment(i)}
                title={strings.email_remove || "Remove"}
              >
                <MaterialIcon icon="close" size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Compose body + original message below in single scrollable area */}
      <div className="email-inline-reply-body">
        <EmailReplySuggestions
          email={email}
          enabled={mode === "reply" || mode === "replyAll"}
          disabled={sending}
          hasDraft={() => editorHasUserDraft(richBodyRef.current)}
          onSelect={(text) => {
            const html = insertEmailReplySuggestion(richBodyRef.current, text);
            setForm((f) => ({ ...f, body: html }));
          }}
        />
        <div className="email-wysiwyg-toolbar">
          <button type="button" className={`email-wysiwyg-btn${activeFormats.bold ? ' active' : ''}`} title="Bold" onMouseDown={(e) => { e.preventDefault(); applyFormat("bold"); }}><MaterialIcon icon="format_bold" size={18} /></button>
          <button type="button" className={`email-wysiwyg-btn${activeFormats.italic ? ' active' : ''}`} title="Italic" onMouseDown={(e) => { e.preventDefault(); applyFormat("italic"); }}><MaterialIcon icon="format_italic" size={18} /></button>
          <button type="button" className={`email-wysiwyg-btn${activeFormats.underline ? ' active' : ''}`} title="Underline" onMouseDown={(e) => { e.preventDefault(); applyFormat("underline"); }}><MaterialIcon icon="format_underlined" size={18} /></button>
          <button type="button" className={`email-wysiwyg-btn${activeFormats.strikethrough ? ' active' : ''}`} title="Strikethrough" onMouseDown={(e) => { e.preventDefault(); applyFormat("strikethrough"); }}><MaterialIcon icon="strikethrough_s" size={18} /></button>
          <span className="email-wysiwyg-separator" />
          <button type="button" className={`email-wysiwyg-btn${activeFormats.unordered ? ' active' : ''}`} title="Bullet list" onMouseDown={(e) => { e.preventDefault(); applyFormat("unordered"); }}><MaterialIcon icon="format_list_bulleted" size={18} /></button>
          <button type="button" className={`email-wysiwyg-btn${activeFormats.ordered ? ' active' : ''}`} title="Numbered list" onMouseDown={(e) => { e.preventDefault(); applyFormat("ordered"); }}><MaterialIcon icon="format_list_numbered" size={18} /></button>
          <span className="email-wysiwyg-separator" />
          <button type="button" className="email-wysiwyg-btn" title="Link" onMouseDown={(e) => { e.preventDefault(); applyFormat("link"); }}><MaterialIcon icon="link" size={18} /></button>
        </div>
        <div
          ref={richBodyRef}
          className="email-rich-editor"
          contentEditable={!sending}
          suppressContentEditableWarning
          data-placeholder={mode === "new"
            ? (strings.email_compose_placeholder || "Write your message...")
            : (mode === "reply" || mode === "replyAll")
              ? (strings.email_reply_placeholder || "Type your reply...")
              : (strings.email_forward_placeholder || "Add a message...")}
          onInput={(e) => {
            handleComposeLinkifyInput(richBodyRef.current, e.nativeEvent);
            const html = richBodyRef.current?.innerHTML || "";
            setForm((f) => ({ ...f, body: html }));
          }}
          onBlur={() => {
            if (!richBodyRef.current) return;
            linkifyElement(richBodyRef.current);
            const html = richBodyRef.current.innerHTML || "";
            setForm((f) => (f.body === html ? f : { ...f, body: html }));
          }}
          onClick={(e) => handleComposeLinkClick(e, richBodyRef.current)}
          onPaste={handlePaste}
        />

        {/* Original message (Outlook-style) — replies/forwards only, never drafts */}
        {email && mode !== "new" && mode !== "draft" && (
          <div className="email-inline-reply-original">
            <div className="email-inline-reply-separator">
              <hr />
            </div>
            <div className="email-inline-reply-original-headers">
              <div><strong>{strings.email_from || "From"}:</strong> {getSenderDisplay()}</div>
              <div><strong>{strings.email_date || "Date"}:</strong> {formatOriginalDate(email.date || email.receivedAt)}</div>
              {(() => {
                const toLine = formatRecipientList(email.toAddresses, email.to);
                return toLine ? <div><strong>{strings.email_to || "To"}:</strong> {toLine}</div> : null;
              })()}
              {(() => {
                const ccLine = formatRecipientList(email.ccAddresses, email.cc);
                return ccLine ? <div><strong>Cc:</strong> {ccLine}</div> : null;
              })()}
              {(() => {
                const bccLine = formatRecipientList(email.bccAddresses, email.bcc);
                return bccLine ? <div><strong>Bcc:</strong> {bccLine}</div> : null;
              })()}
              <div><strong>{strings.email_subject || "Subject"}:</strong> {email.subject || ""}</div>
            </div>
            <div className="email-inline-reply-original-body">
              <iframe
                ref={originalIframeRef}
                className="email-inline-reply-original-iframe"
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                title="Original email"
                scrolling="no"
              />
            </div>
          </div>
        )}
      </div>
      </div>

      {(sending || savingDraft) && (
        <div className="email-action-overlay">
          <span className="material-symbols-outlined email-transition-icon">
            {sending ? "send" : "drafts"}
          </span>
          <div className="email-transition-text">
            {sending
              ? (strings.email_sending || "Sending email...")
              : (strings.email_saving_draft || "Saving draft...")}
          </div>
          <div className="email-transition-shimmer" />
        </div>
      )}

      {isDragOver && (
        <div className="email-compose-drop-overlay">
          <div className="email-compose-drop-overlay-content">
            <MaterialIcon icon="upload_file" size={36} />
            <span>{strings.email_drop_files || "Drop files to attach"}</span>
          </div>
        </div>
      )}
    </div>
  );
});

const LOCALE_MAP = { de: "de-DE", en: "en-GB", fr: "fr-FR", it: "it-IT", es: "es-ES", pt: "pt-PT", nl: "nl-NL", pl: "pl-PL", ru: "ru-RU", ja: "ja-JP", ko: "ko-KR", zh: "zh-CN", ar: "ar-SA", tr: "tr-TR", sv: "sv-SE", da: "da-DK", no: "nb-NO", fi: "fi-FI", cs: "cs-CZ", hu: "hu-HU", ro: "ro-RO", bg: "bg-BG", hr: "hr-HR", sl: "sl-SI", el: "el-GR", id: "id-ID", ms: "ms-MY", vi: "vi-VN" };

function emailIdentityKeys(email) {
  if (!email) return [];
  return [email.id, email._id, email.messageId, email.providerMessageId]
    .filter((x) => x != null && String(x).trim() !== "")
    .map(String);
}

function emailsShareIdentity(a, b) {
  if (!a || !b) return false;
  const other = new Set(emailIdentityKeys(b));
  return emailIdentityKeys(a).some((key) => other.has(key));
}

// Kept outside EmailDetailView: every `&&` / `||` in the component body adds
// code paths, and past a limit react-hooks/rules-of-hooks overflows and flags
// all later hooks as "called conditionally", which fails the build.
function canClickToEditDraftFor({ email, activeFolder, replyMode, composeSource }) {
  const isDraftMessage = String(activeFolder || "").toUpperCase() === "DRAFT"
    || String(email?.folder || "").toUpperCase() === "DRAFT"
    || (Array.isArray(email?.labels) && email.labels.some((l) => String(l).toUpperCase() === "DRAFT"));
  const viewingEditedDraft = replyMode === "draft" && emailsShareIdentity(composeSource || email, email);
  return isDraftMessage && (!replyMode || viewingEditedDraft);
}

function emailRecordId(em) {
  if (!em) return "";
  return String(em.id || em._id || em.messageId || em.providerMessageId || "").trim();
}

function ThreadMessageCard({
  message,
  strings,
  formatFullDate,
  isBookmarked,
  onToggleBookmark,
  onReply,
  onReplyAll,
  onForward,
  expanded,
  onToggleExpand,
}) {
  const sender = formatEmailSenderDisplay(message);
  const snippet = String(message.bodyText || message.snippet || "").replace(/\s+/g, " ").trim();
  return (
    <div className={`email-thread-card${expanded ? " is-expanded" : ""}`}>
      <div className="email-thread-card-header" onClick={onToggleExpand} role="button" tabIndex={0}>
        <div className="email-thread-card-meta">
          <strong>{sender}</strong>
          <span className="email-thread-card-date">{formatFullDate(message.date || message.receivedAt)}</span>
        </div>
        <div className="email-thread-card-actions" onClick={(e) => e.stopPropagation()}>
          {onReply && (
            <button className="kj-btn kj-btn--icon kj-btn--neutral kj-btn--email kj-btn--round" title={strings.email_reply || "Reply"} onClick={() => onReply(message)}>
              <MaterialIcon icon="reply" size={16} />
            </button>
          )}
          {onReplyAll && (
            <button className="kj-btn kj-btn--icon kj-btn--neutral kj-btn--email kj-btn--round" title={strings.email_reply_all || "Reply all"} onClick={() => onReplyAll(message)}>
              <MaterialIcon icon="reply_all" size={16} />
            </button>
          )}
          {onForward && (
            <button className="kj-btn kj-btn--icon kj-btn--neutral kj-btn--email kj-btn--round" title={strings.email_forward || "Forward"} onClick={() => onForward(message)}>
              <MaterialIcon icon="forward" size={16} />
            </button>
          )}
          {onToggleBookmark && (
            <button
              className="kj-btn kj-btn--icon kj-btn--neutral kj-btn--email kj-btn--round"
              title={isBookmarked ? strings.unmark_task || "Remove from tasks" : strings.mark_as_task || "Mark as task"}
              onClick={() => onToggleBookmark(message)}
            >
              <MaterialIcon icon={isBookmarked ? "task_alt" : "add_task"} size={16} color={isBookmarked ? "#e066ed" : undefined} fill={isBookmarked ? 1 : 0} />
            </button>
          )}
        </div>
      </div>
      {!expanded && snippet ? (
        <div className="email-thread-card-snippet">{snippet.slice(0, 180)}</div>
      ) : null}
      {expanded ? (
        <div className="email-thread-card-body">
          {formatEmailPlainBody(message.bodyText || snippet)}
        </div>
      ) : null}
    </div>
  );
}

const EmailDetailView = forwardRef(function EmailDetailView({ email, onClose, onReply, onReplyAll, onForward, onDelete, onNotSpam, onMarkSpam, onArchive, onMarkUnread, onMoveEmail, onToggleStar, onOpenProject, onChangeProject, onRemoveProject, onOpenChatLink, projectColorById, customFolders, activeFolder, replyMode, onCancelReply, onEditDraft = null, accountId, emailAccounts, onSent, buildings = [], initialBuildingId = null, documentMode = false, companionContent = null, companionTab = null, onToggleBookmark = null, bookmarkProjectId = null }, ref) {
  const { strings, language } = useContext(LanguageContext);
  const locale = LOCALE_MAP[language?.toLowerCase()] || LOCALE_MAP.en;
  const iframeRef = useRef(null);
  const inlineComposeRef = useRef(null);
  const detailScrollRef = useRef(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [attachmentMeta, setAttachmentMeta] = useState({});
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadAllProgress, setDownloadAllProgress] = useState(0);
  const [detailMoveDropdownOpen, setDetailMoveDropdownOpen] = useState(false);
  const detailMoveDropdownRef = useRef(null);
  // "Verschieben nach" folds its folder list open inside the "…" menu.
  const [detailMoveListOpen, setDetailMoveListOpen] = useState(false);
  // Clicked project tag → shows the navigate/change chooser.
  const [tagActionProject, setTagActionProject] = useState(null);
  const apiBaseUrl = API_ROOT;
  const isNewCompose = replyMode === "new" || replyMode === "draft";
  const onOpenChatLinkRef = useRef(onOpenChatLink);
  onOpenChatLinkRef.current = onOpenChatLink;
  const [composeSource, setComposeSource] = useState(null);
  const [threadEmails, setThreadEmails] = useState([]);
  const [expandedThreadIds, setExpandedThreadIds] = useState(() => new Set());
  const [localBookmarkedIds, setLocalBookmarkedIds] = useState(() => new Set());

  useEffect(() => {
    const persistId = email && isPersistedEmailId(email.id || email._id) ? (email.id || email._id) : null;
    if (!persistId || !email?.threadId) {
      setThreadEmails([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await callAPI(`/email/messages/${persistId}/thread`, {}, "GET");
        if (cancelled || !data?.status) return;
        const messages = Array.isArray(data.messages) ? data.messages : [];
        const currentId = emailRecordId(email);
        const others = messages
          .filter((m) => emailRecordId(m) && emailRecordId(m) !== currentId)
          .sort((a, b) => new Date(b.date || b.receivedAt || 0) - new Date(a.date || a.receivedAt || 0));
        setThreadEmails(others);
        setLocalBookmarkedIds((prev) => {
          const next = new Set(prev);
          messages.forEach((m) => {
            const id = emailRecordId(m);
            if (id && m.isTodoBookmarked) next.add(id);
          });
          if (email.isTodoBookmarked && currentId) next.add(currentId);
          return next;
        });
      } catch (_) {
        if (!cancelled) setThreadEmails([]);
      }
    })();
    return () => { cancelled = true; };
  }, [email?.id, email?._id, email?.threadId, email?.isTodoBookmarked]);

  const handleReplyFrom = (mode, sourceEmail) => {
    if (sourceEmail) setComposeSource(sourceEmail);
    if (mode === "replyAll" && onReplyAll) onReplyAll(sourceEmail);
    else if (mode === "forward" && onForward) onForward(sourceEmail);
    else if (onReply) onReply(sourceEmail);
  };

  // "Mark as task" needs a project: the one given (a project's own mail), else
  // the email's. A message of the thread carries the project of the open email.
  const threadProjectId = bookmarkProjectId || emailProjectIdOf(email);
  const canBookmarkEmail = (bookmarked) => !!onToggleBookmark && (bookmarked || !!threadProjectId);

  const handleToggleBookmark = (sourceEmail) => {
    if (!sourceEmail || !onToggleBookmark) return;
    if (!emailProjectIdOf(sourceEmail) && threadProjectId) {
      sourceEmail = { ...sourceEmail, buildingId: threadProjectId };
    }
    const id = emailRecordId(sourceEmail);
    setLocalBookmarkedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    onToggleBookmark(sourceEmail);
  };

  useEffect(() => {
    if (!replyMode) {
      setComposeSource(null);
      return;
    }
    if (replyMode === "new") return;
    setComposeSource((prev) => {
      if (!prev) return email || null;
      if (emailsShareIdentity(prev, email)) {
        return { ...prev, ...email };
      }
      return prev;
    });
  }, [replyMode, email]);

  // Build the correct attachment URL. INBOX attachments have a local DB row
  // (`attachment.id`); provider-only folders (Sent / Drafts / Trash / Spam) are
  // fetched live and only carry a `providerAttachmentId` — those must stream
  // from the account-scoped provider endpoint, with the source folder so the BE
  // opens the right mailbox (IMAP). `endpoint` is 'preview' | 'download'.
  const buildAttachmentUrl = useCallback((attachment, endpoint) => {
    const base = String(apiBaseUrl || "").replace(/\/$/, "");
    if (attachment?.id) {
      return `${base}/email/attachments/${attachment.id}/${endpoint}`;
    }
    const accId = getEmailAccountId(email) || accountId;
    const providerMessageId = email?.providerMessageId || attachment?.providerMessageId || "";
    const folder = email?.folder || activeFolder || "INBOX";
    const params = new URLSearchParams({
      providerMessageId: String(providerMessageId),
      providerAttachmentId: String(attachment?.providerAttachmentId || ""),
      filename: String(attachment?.filename || attachment?.name || ""),
      mimeType: String(attachment?.contentType || attachment?.mimeType || ""),
      size: String(attachment?.size || attachment?.fileSize || ""),
      disposition: endpoint === "preview" ? "inline" : "attachment",
      folder: String(folder),
    });
    if (attachment?.contentId) params.append("contentId", String(attachment.contentId));
    return `${base}/email/accounts/${accId}/provider-attachment?${params.toString()}`;
  }, [apiBaseUrl, email, accountId, activeFolder]);
  const canClickToEditDraft = canClickToEditDraftFor({ email, activeFolder, replyMode, composeSource });

  const [activeTab, setActiveTab] = useState(replyMode ? "compose" : "detail");

  const emailIdKey = email && (email.id || email._id || email.messageId || email.providerMessageId);
  const prevEmailIdRef = useRef(emailIdKey);
  const companionKey = companionTab?.key || null;
  const prevCompanionKeyRef = useRef(companionKey);

  const beginDraftEdit = useCallback(() => {
    if (replyMode === "draft") {
      setActiveTab("compose");
      requestAnimationFrame(() => {
        inlineComposeRef.current?.focusEditor?.();
      });
      return;
    }
    if (typeof onEditDraft === "function") onEditDraft();
  }, [replyMode, onEditDraft]);

  // Read through refs in the body effect below. `onEditDraft` is passed as an
  // inline arrow, so `beginDraftEdit` is a new function on every render of the
  // parent; depending on it directly made the effect rewrite the whole iframe
  // continuously, wiping any text the reader had selected.
  const beginDraftEditRef = useRef(beginDraftEdit);
  beginDraftEditRef.current = beginDraftEdit;
  const canClickToEditDraftRef = useRef(canClickToEditDraft);
  canClickToEditDraftRef.current = canClickToEditDraft;
  const writtenBodyRef = useRef(null);

  useEffect(() => {
    if (replyMode) {
      setActiveTab("compose");
    } else {
      setActiveTab("detail");
    }
  }, [replyMode]);

  useEffect(() => {
    if (prevEmailIdRef.current !== emailIdKey) {
      const prev = prevEmailIdRef.current;
      prevEmailIdRef.current = emailIdKey;
      if (replyMode && emailIdKey != null && String(prev) !== String(emailIdKey)) {
        // New-mail compose has no source message. Drafts open in the reader
        // so a list click does not jump into the editor; click the body to edit.
        const stayOnCompose = replyMode === "new";
        if (!stayOnCompose) {
          setActiveTab("detail");
        }
      } else if (replyMode && emailIdKey == null && !companionKey) {
        setActiveTab("compose");
      }
      setDetailMoveDropdownOpen(false);
      setIsDownloadingAll(false);
      setDownloadAllProgress(0);
      if (detailScrollRef.current) {
        detailScrollRef.current.scrollTop = 0;
      }
    }
  }, [emailIdKey, replyMode, companionKey, composeSource, email]);

  useEffect(() => {
    if (prevCompanionKeyRef.current === companionKey) return;
    const hadCompanion = !!prevCompanionKeyRef.current;
    prevCompanionKeyRef.current = companionKey;
    if (!replyMode) return;
    if (companionKey) {
      setActiveTab("detail");
    } else if (hadCompanion && !emailIdKey) {
      setActiveTab("compose");
    }
  }, [companionKey, replyMode, emailIdKey]);

  useEffect(() => {
    if (!detailMoveDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (detailMoveDropdownRef.current && !detailMoveDropdownRef.current.contains(e.target)) {
        setDetailMoveDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [detailMoveDropdownOpen]);

  const handleDetailMove = (targetFolder) => {
    if (!email || !onMoveEmail) return;
    const emailId = email.id || email._id || email.messageId || email.providerMessageId;
    setDetailMoveDropdownOpen(false);
    onMoveEmail([emailId], targetFolder);
  };

  /** Copy the highlighted part of the message, or the whole body when nothing
   *  is highlighted. Reads from the rendered iframe so the copied text matches
   *  what is on screen (quoted history formatting, links resolved). */
  const handleCopyBody = useCallback(async () => {
    if (!email) return;
    const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
    const selected = doc ? String(doc.getSelection() || "").trim() : "";
    const rendered = String(doc?.body?.innerText || "").trim();
    const text = selected || rendered || formatEmailPlainBody(resolveEmailBody(email));
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const helper = document.createElement("textarea");
        helper.value = text;
        helper.setAttribute("readonly", "");
        helper.style.position = "fixed";
        helper.style.opacity = "0";
        document.body.appendChild(helper);
        helper.select();
        document.execCommand("copy");
        document.body.removeChild(helper);
      }
      $crud.notify({ type: "success", message: strings.email_text_copied || "Copied!" });
    } catch (err) {
      $crud.notify({ type: "error", message: strings.email_text_copy_failed || "Could not copy the text." });
    }
  }, [email, strings]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (activeTab === "detail" && iframe && email) {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        const rawBody = resolveEmailBody(email);
        const isHtml = looksLikeHtml(rawBody);
        const prepared = isHtml ? prepareEmailHtml(rawBody) : { styles: '', body: rawBody };
        const emailStyles = prepared.styles;
        const emailBody = (documentMode || isDailyBriefingEmail(email))
          ? stripMailHeaderPreamble(prepared.body)
          : prepared.body;
        const html = buildEmailIframeDocument({
          styles: emailStyles,
          body: isHtml ? emailBody : `<pre style="white-space:pre-wrap;font-family:inherit;">${linkifyPlainText(emailBody)}</pre>`,
          fontSize: "var(--kjuup-type-body)",
        });
        // doc.open() discards every node in the iframe, and with them the
        // reader's text selection and scroll position. Only pay that price
        // when the rendered mail actually changed. An empty body means the
        // iframe was remounted and has to be filled again.
        if (writtenBodyRef.current !== html || !doc.body || !doc.body.firstChild) {
          doc.open();
          doc.write(html);
          doc.close();
          writtenBodyRef.current = html;
          cleanupEmailImages(doc);
          makeLinksOpenNewTab(doc);
          if (!(documentMode || isDailyBriefingEmail(email))) {
            formatEmailConversationHistory(doc);
          }
        }
        const onDraftBodyClick = (evt) => {
          if (!canClickToEditDraftRef.current) return;
          if (evt.target?.closest?.("a")) return;
          beginDraftEditRef.current();
        };
        doc.addEventListener("click", onDraftBodyClick);
        return combineCleanups(
          resizeIframeToContent(iframe),
          attachIframeScrollRelay(iframe),
          attachEmailChatLinkHandler(doc, (params) => {
            if (typeof onOpenChatLinkRef.current === "function") {
              onOpenChatLinkRef.current(params);
              return;
            }
            if (typeof window === "undefined" || !params || !params.chatGroupId) return;
            const qs = new URLSearchParams();
            qs.set("chatGroupId", params.chatGroupId);
            if (params.messageId) qs.set("messageId", params.messageId);
            if (params.roomId) qs.set("roomId", params.roomId);
            if (params.companyId) qs.set("companyId", params.companyId);
            window.location.assign(`/deeplink?${qs.toString()}`);
          }),
          () => doc.removeEventListener("click", onDraftBodyClick)
        );
      }
    }
    return undefined;
  }, [email, activeTab, documentMode, replyMode, composeSource]);

  const fetchAttachmentMeta = useCallback(async (attachmentId) => {
    if (attachmentMeta[attachmentId]) return;
    try {
      const { data } = await callAPI(`/email/attachments/${attachmentId}/metadata`, {}, "GET");
      if (data?.status && data?.attachment) {
        setAttachmentMeta((prev) => ({ ...prev, [attachmentId]: data.attachment }));
      }
    } catch (err) {
      console.error("Failed to fetch attachment metadata:", err);
    }
  }, [attachmentMeta]);

  useEffect(() => {
    if (email?.attachments?.length > 0) {
      email.attachments.forEach((att) => {
        // Metadata endpoint requires a local DB id — provider-only attachments
        // (Sent/Drafts/Trash/Spam) don't have one, so skip to avoid a 404 on
        // `/email/attachments/undefined/metadata`.
        if (att?.id) fetchAttachmentMeta(att.id);
      });
    }
  }, [email?.attachments]);

  const handleDownload = async (attachment) => {
    try {
      const token = require("../helper/UserFactory").$user.getToken();
      const res = await fetch(buildAttachmentUrl(attachment, "download"), {
        headers: { Authorization: `jwt ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      // Force a save (octet-stream) so PDFs/Office docs don't open in a preview tab.
      triggerBlobDownload(blob, attachment.filename || "download");
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleDownloadAll = async () => {
    const list = email?.attachments || [];
    if (isDownloadingAll || list.length < 2) return;
    setIsDownloadingAll(true);
    setDownloadAllProgress(0);
    try {
      const token = require("../helper/UserFactory").$user.getToken();
      const zip = new JSZip();
      const usedNames = new Set();
      const total = list.length;
      let added = 0;
      for (let i = 0; i < total; i++) {
        const att = list[i];
        try {
          const res = await fetch(buildAttachmentUrl(att, "download"), {
            headers: { Authorization: `jwt ${token}` },
          });
          if (!res.ok) throw new Error(`Download failed (${res.status})`);
          const blob = await res.blob();
          zip.file(uniqueZipEntryName(att.filename || att.name, i, usedNames), blob);
          added++;
        } catch (err) {
          console.error("Error adding attachment to zip:", err);
        }
        setDownloadAllProgress(Math.round(((i + 1) / total) * 80));
      }
      if (added === 0) return;
      const zipBlob = await zip.generateAsync({ type: "blob" }, (metadata) => {
        setDownloadAllProgress(80 + Math.round((metadata.percent || 0) * 0.2));
      });
      setDownloadAllProgress(100);
      triggerBlobDownload(zipBlob, sanitizeZipFilename(email?.subject));
    } catch (err) {
      console.error("Error creating attachments zip:", err);
    } finally {
      setIsDownloadingAll(false);
      setDownloadAllProgress(0);
    }
  };

  const handlePreview = (attachment) => {
    const meta = attachmentMeta[attachment.id];
    const mimeType = meta?.mimeType || attachment.contentType || attachment.mimeType || "";
    if (PREVIEWABLE_TYPES.includes(mimeType)) {
      // Pre-resolve the stream URLs so the modal works for both local and
      // provider-only attachments without needing the message context.
      setPreviewAttachment({
        ...attachment,
        ...meta,
        __previewUrl: buildAttachmentUrl(attachment, "preview"),
        __downloadUrl: buildAttachmentUrl(attachment, "download"),
      });
    } else {
      handleDownload(attachment);
    }
  };

  const handleAttachmentDragStart = (e, attachment) => {
    const meta = attachmentMeta[attachment.id];
    const mimeType = meta?.mimeType || attachment.contentType || "application/octet-stream";
    const filename = attachment.filename || "download";
    const token = require("../helper/UserFactory").$user.getToken();
    const base = buildAttachmentUrl(attachment, "download");
    const downloadUrl = `${base}${base.includes("?") ? "&" : "?"}token=${token}`;
    e.dataTransfer.setData("DownloadURL", `${mimeType}:${filename}:${downloadUrl}`);
    e.dataTransfer.effectAllowed = "copy";
  };

  useImperativeHandle(ref, () => ({
    requestCloseCompose: async () => {
      if (!replyMode) return true;
      if (!inlineComposeRef.current?.requestClose) {
        onCancelReply();
        return true;
      }
      const closed = await inlineComposeRef.current.requestClose();
      if (!closed) setActiveTab("compose");
      return closed;
    },
  }), [replyMode, onCancelReply]);

  if (!replyMode && !email) {
    const noEmailsSelectedText = `${strings.email_no_messages || "No emails"} ${String(
      strings.selected || "selected"
    ).toLowerCase()}`;
    return (
      <div className="email-detail-empty">
        <MaterialIcon icon="mail_off" size={48} color="#ccc" className="email-detail-view__empty-icon" />
        <p className="email-detail-view__empty-text">
          {noEmailsSelectedText}
        </p>
      </div>
    );
  }

  const getSenderDisplay = () => formatEmailSenderDisplay(email, activeFolder);

  const formatFullDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString(locale, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const attachments = email ? (email.attachments || []) : [];
  const isBriefing = documentMode || isDailyBriefingEmail(email);
  const showBottomTabs = !!replyMode;
  const showCompanionTab = !!(companionContent && companionTab);
  const showViewedEmailTab = !showCompanionTab && !!email && replyMode !== "new" && (
    replyMode !== "draft" || (composeSource && !emailsShareIdentity(composeSource, email))
  );
  const composeTabLabel = replyMode === "draft"
    ? (strings.email_edit_draft || strings.new_email || "Draft")
    : isNewCompose
    ? (strings.new_email || "New E-Mail")
    : replyMode === "reply"
      ? (strings.email_reply || "Reply")
      : replyMode === "replyAll"
        ? (strings.email_reply_all || "Reply all")
        : (strings.email_forward || "Forward");
  const composeTabIcon = isNewCompose || replyMode === "draft" ? "edit" : replyMode === "replyAll" ? "reply_all" : "reply";

  return (
    <div className={`email-detail${isBriefing ? " email-detail--document" : ""}`}>
      {/* Top toolbar — only shown in detail tab when an email is selected */}
      {email && !showCompanionTab && (!replyMode || activeTab === "detail") && (
        <>
          <div className="email-detail-toolbar">
            <div className="email-detail-toolbar-left">
              <button
                className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--round"
                onClick={onClose}
                title={strings.email_back || "Back"}
              >
                <MaterialIcon icon="arrow_back" size={20} />
              </button>
              {email.systemTag === "daily_briefing" && (
                <span className="email-detail-project-tags">
                  <span className="email-item-briefing-tag" title={strings.email_tag_daily_briefing || "Daily report"}>
                    <MaterialIcon icon="newspaper" size={12} color="#ffffff" />
                    <span>{strings.email_tag_daily_briefing || "Daily report"}</span>
                  </span>
                </span>
              )}
              {email.systemTag !== "daily_briefing" && Array.isArray(email.linkedProjects) && email.linkedProjects.length > 0 && (
                <span className="email-detail-project-tags">
                  {email.linkedProjects.map((p) => {
                    const color = (p && p.themeColor) || (projectColorById && projectColorById.get(String(p?.id))) || null;
                    const tc = getProjectTagColors(color);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className="email-detail-project-tag email-detail-view__project-tag"
                        title={p.name}
                        style={{ "--email-tag-bg": tc.bg, "--email-tag-fg": tc.fg }}
                        onClick={() => setTagActionProject(p)}
                      >
                        <MaterialIcon icon="folder" size={12} color={tc.fg} />
                        <span>{p.name}</span>
                      </button>
                    );
                  })}
                </span>
              )}
            </div>
            <div className="email-detail-actions">
              {!isBriefing && onReply && (
                <button
                  className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--email kj-btn--round"
                  title={strings.email_reply || "Reply"}
                  onClick={() => onReply(email)}
                >
                  <MaterialIcon icon="reply" size={18} />
                </button>
              )}
              {!isBriefing && onReplyAll && (
                <button
                  className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--email kj-btn--round"
                  title={strings.email_reply_all || "Reply all"}
                  onClick={() => onReplyAll(email)}
                >
                  <MaterialIcon icon="reply_all" size={18} />
                </button>
              )}
              {!isBriefing && onForward && (
                <button
                  className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--email kj-btn--round"
                  title={strings.email_forward || "Forward"}
                  onClick={() => onForward(email)}
                >
                  <MaterialIcon icon="forward" size={18} />
                </button>
              )}
              {!isBriefing && onMoveEmail && activeFolder === "TRASH" && (
                <button
                  data-testid="actionRestoreToInbox"
                  className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--email kj-btn--round"
                  title={strings.email_move_to_inbox || "Move to Inbox"}
                  onClick={() => handleDetailMove("INBOX")}
                >
                  <MaterialIcon icon="move_to_inbox" size={18} color="#e066ed" />
                </button>
              )}
              {!isBriefing && canBookmarkEmail(!!(email.isTodoBookmarked || localBookmarkedIds.has(emailRecordId(email)))) && (
                <button
                  className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--email kj-btn--round"
                  title={(email.isTodoBookmarked || localBookmarkedIds.has(emailRecordId(email))) ? strings.unmark_task || "Remove from tasks" : strings.mark_as_task || "Mark as task"}
                  onClick={() => handleToggleBookmark(email)}
                >
                  <MaterialIcon
                    icon={(email.isTodoBookmarked || localBookmarkedIds.has(emailRecordId(email))) ? "task_alt" : "add_task"}
                    size={18}
                    color={(email.isTodoBookmarked || localBookmarkedIds.has(emailRecordId(email))) ? "#e066ed" : undefined}
                    fill={(email.isTodoBookmarked || localBookmarkedIds.has(emailRecordId(email))) ? 1 : 0}
                  />
                </button>
              )}
              {/* Everything but replying, forwarding and "Als Aufgabe" sits
                  behind "…" at the end of the toolbar. */}
              <div className="email-move-dropdown-wrap" ref={detailMoveDropdownRef}>
                <button
                  data-testid="actionMore"
                  className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--email kj-btn--round"
                  title={strings.email_more_actions || "Weitere Aktionen"}
                  aria-haspopup="menu"
                  aria-expanded={detailMoveDropdownOpen}
                  onClick={() => {
                    setDetailMoveListOpen(false);
                    setDetailMoveDropdownOpen((v) => !v);
                  }}
                >
                  <MaterialIcon icon="more_vert" size={18} />
                </button>
                {detailMoveDropdownOpen && (
                  <div className="email-move-dropdown email-detail-more-menu" role="menu">
                    {(() => {
                      const emailId = email.id || email._id || email.messageId || email.providerMessageId;
                      const entry = (key, icon, label, run, extra = {}) => (
                        <div
                          key={key}
                          role="menuitem"
                          tabIndex={0}
                          data-testid={extra.testId}
                          className={`email-move-dropdown-item${extra.danger ? " email-detail-more-menu__item--danger" : ""}`}
                          onClick={() => {
                            if (!extra.keepOpen) setDetailMoveDropdownOpen(false);
                            run();
                          }}
                        >
                          <MaterialIcon icon={icon} size={16} color={extra.color} />
                          <span className="email-detail-more-menu__label" style={{ textAlign: "left" }}>{label}</span>
                          {extra.chevron ? <MaterialIcon icon={extra.chevron} size={16} /> : null}
                        </div>
                      );
                      const moveTargets = onMoveEmail && activeFolder !== "TRASH"
                        ? [
                          activeFolder !== "INBOX" ? { id: "INBOX", icon: "inbox", label: strings.email_folder_inbox || "Inbox" } : null,
                          activeFolder !== "ARCHIVE" ? { id: "ARCHIVE", icon: "archive", label: strings.email_folder_archive || "Archive" } : null,
                          activeFolder !== "SPAM" ? { id: "SPAM", icon: "report", label: strings.email_folder_spam || "Spam" } : null,
                          { id: "TRASH", icon: "delete", label: strings.email_folder_deleted || "Trash" },
                          ...(customFolders || []).map((f) => ({ id: f.providerFolderId, icon: "folder", label: f.name, key: f.id })),
                        ].filter(Boolean)
                        : [];
                      return [
                        entry("copy", "content_copy", strings.email_copy_text || "Copy text", handleCopyBody, { testId: "actionCopyText" }),
                        onChangeProject && email.systemTag !== "daily_briefing"
                          ? entry("project", "create_new_folder", strings.email_classify_associate || "Associate to project", () => onChangeProject(email), { testId: "actionAssignProject" })
                          : null,
                        !isBriefing && moveTargets.length
                          ? entry("move", "drive_file_move", strings.email_move_to || "Move to", () => setDetailMoveListOpen((v) => !v), {
                            testId: "actionMove",
                            keepOpen: true,
                            chevron: detailMoveListOpen ? "expand_less" : "expand_more",
                          })
                          : null,
                        !isBriefing && detailMoveListOpen
                          ? moveTargets.map((target) => (
                            <div
                              key={`move-${target.key || target.id}`}
                              role="menuitem"
                              tabIndex={0}
                              data-testid={`moveSheetRow-${String(target.label || "").toUpperCase()}`}
                              className="email-move-dropdown-item email-detail-more-menu__sub"
                              onClick={() => handleDetailMove(target.id)}
                            >
                              <MaterialIcon icon={target.icon} size={16} />
                              <span className="email-detail-more-menu__label" style={{ textAlign: "left" }}>{target.label}</span>
                            </div>
                          ))
                          : null,
                        !isBriefing && onArchive && activeFolder !== "ARCHIVE" && activeFolder !== "TRASH"
                          ? entry("archive", "archive", strings.email_archive || "Archive", () => onArchive([emailId]), { testId: "actionArchive" })
                          : null,
                        !isBriefing && onMarkUnread
                          ? entry("unread", "mark_email_unread", strings.mark_unread || "Mark as unread", () => onMarkUnread([emailId]), { testId: "actionMarkUnread" })
                          : null,
                        !isBriefing && onMarkSpam && activeFolder !== "SPAM"
                          ? entry("spam", "report", strings.email_mark_spam || "Mark as spam", () => onMarkSpam([emailId]), { testId: "actionMarkSpam" })
                          : null,
                        !isBriefing && onMoveEmail && activeFolder === "TRASH"
                          ? entry("restore", "move_to_inbox", strings.email_move_to_inbox || "Move to Inbox", () => handleDetailMove("INBOX"), { testId: "actionMenuRestoreToInbox", color: "#e066ed" })
                          : null,
                        onNotSpam && activeFolder === "SPAM"
                          ? entry("notspam", "move_to_inbox", strings.email_not_spam || "Not Spam", () => onNotSpam(email), { testId: "actionNotSpam", color: "#e066ed" })
                          : null,
                        onDelete
                          ? entry("delete", "delete", strings.email_delete || "Delete", () => onDelete(email), { danger: true })
                          : null,
                      ];
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main content area */}
      <div className="email-detail-view__pane">
        {replyMode && (
          <div className={`email-detail-view__pane${activeTab === "compose" ? "" : " email-detail-view__pane--hidden"}`}>
            <InlineReplyCompose
              key={replyMode}
              ref={inlineComposeRef}
              email={replyMode === "new" ? null : (composeSource || email)}
              mode={replyMode}
              accountId={accountId}
              emailAccounts={emailAccounts}
              onSent={onSent}
              onCancel={onCancelReply}
              buildings={buildings}
              initialBuildingId={replyMode === "new" ? initialBuildingId : null}
            />
          </div>
        )}
        {showCompanionTab && (
          <div
            className={`email-detail-companion${activeTab === "detail" || !replyMode ? "" : " email-detail-view__companion--hidden"}`}
          >
            {companionContent}
          </div>
        )}
        {email && !showCompanionTab ? (
          <div className={`email-detail-view__pane${activeTab === "detail" || !replyMode ? "" : " email-detail-view__pane--hidden"}`}>
            <div
              ref={detailScrollRef}
              className={`email-detail-scroll${canClickToEditDraft ? " email-detail-scroll--draft-edit" : ""}`}
              onClick={canClickToEditDraft ? (e) => {
                if (e.target.closest("button, a, .email-attachment-chip, .email-attachments-download-all")) return;
                beginDraftEdit();
              } : undefined}
              title={canClickToEditDraft ? (strings.email_edit_draft || "Edit draft") : undefined}
            >
              {!isBriefing && (
              <div className="email-detail-header">
                <div className="email-detail-subject-row">
                  <h2 className="email-detail-subject">{email.subject || (strings.email_no_subject || "(No subject)")}</h2>
                  {false && onToggleStar && (
                    <button
                      data-testid="emailDetailStar"
                      className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email email-detail-star-btn"
                      title={email.isStarred ? (strings.email_unstar || "Unstar") : (strings.email_star || "Star")}
                      onClick={() => {
                        const eid = email.id || email._id || email.messageId || email.providerMessageId;
                        onToggleStar(eid, !email.isStarred);
                      }}
                    >
                      <MaterialIcon
                        icon={email.isStarred ? "star" : "star_border"}
                        size={22}
                        color={email.isStarred ? "#F2B100" : undefined}
                        fill={email.isStarred ? 1 : 0}
                      />
                    </button>
                  )}
                </div>
                <div className="email-detail-meta">
                  <div className="email-detail-meta-row">
                    <span className="email-detail-meta-label">{strings.email_from}:</span>
                    <span className="email-detail-meta-value">{getSenderDisplay()}</span>
                  </div>
                  {(() => {
                    const toLine = formatRecipientList(email.toAddresses, email.to);
                    return toLine ? (
                      <div className="email-detail-meta-row">
                        <span className="email-detail-meta-label">{strings.email_to}:</span>
                        <span className="email-detail-meta-value">{toLine}</span>
                      </div>
                    ) : null;
                  })()}
                  {(() => {
                    const ccLine = formatRecipientList(email.ccAddresses, email.cc);
                    return ccLine ? (
                      <div className="email-detail-meta-row">
                        <span className="email-detail-meta-label">Cc:</span>
                        <span className="email-detail-meta-value">{ccLine}</span>
                      </div>
                    ) : null;
                  })()}
                  {(() => {
                    const bccLine = formatRecipientList(email.bccAddresses, email.bcc);
                    return bccLine ? (
                      <div className="email-detail-meta-row">
                        <span className="email-detail-meta-label">Bcc:</span>
                        <span className="email-detail-meta-value">{bccLine}</span>
                      </div>
                    ) : null;
                  })()}
                  <div className="email-detail-meta-row">
                    <span className="email-detail-meta-label">{strings.email_date}:</span>
                    <span className="email-detail-meta-value">{formatFullDate(email.date || email.receivedAt)}</span>
                  </div>
                </div>
              </div>
              )}

              {!isBriefing && (attachments.length > 0 || (email.hasAttachments && attachments.length === 0)) && (
                <div className="email-attachments">
                  <div className="email-attachments-header">
                    <div className="email-attachments-header-left">
                      <MaterialIcon icon="attach_file" size={14} />
                      <span>
                        {attachments.length > 0
                          ? `${attachments.length} ${attachments.length === 1 ? (strings.email_attachment_singular || "Attachment") : (strings.email_attachment_plural || "Attachments")}`
                          : (strings.email_attachment_plural || "Attachments")}
                      </span>
                    </div>
                    {attachments.length > 1 && (
                      <button
                        type="button"
                        className="kj-btn kj-btn--secondary kj-btn--sm email-attachments-download-all"
                        onClick={handleDownloadAll}
                        disabled={isDownloadingAll}
                        title={strings.email_download_all_zip || "Download all as ZIP"}
                      >
                        <MaterialIcon
                          icon={isDownloadingAll ? "autorenew" : "folder_zip"}
                          size={14}
                          className={isDownloadingAll ? "email-refresh-spin" : undefined}
                        />
                        <span>
                          {isDownloadingAll
                            ? `${strings.preparing_zip || "Preparing zip…"} ${downloadAllProgress}%`
                            : (strings.email_download_all || "Download all")}
                        </span>
                      </button>
                    )}
                  </div>
                  {attachments.length > 0 ? (
                    <div className="email-attachments-list">
                      {attachments.map((att, attIndex) => {
                        const meta = attachmentMeta[att.id];
                        const mimeType = meta?.mimeType || att.contentType || att.mimeType || "";
                        const fileIconName = getFileIconName(mimeType);
                        const isPreviewable = PREVIEWABLE_TYPES.includes(mimeType);
                        const sizeText = meta ? meta.sizeFormatted : (att.size ? formatBytes(att.size) : "");

                        return (
                          <div
                            key={att.id || att.providerAttachmentId || `att-${attIndex}`}
                            className="email-attachment-chip"
                            draggable={true}
                            onDragStart={(e) => handleAttachmentDragStart(e, att)}
                            onClick={() => handleDownload(att)}
                          >
                            <MaterialIcon icon={fileIconName} size={16} className="email-attachment-icon" />
                            <div className="email-attachment-info">
                              <span className="email-attachment-name" title={att.filename}>{att.filename}</span>
                              {sizeText && <span className="email-attachment-size">{sizeText}</span>}
                            </div>
                            <div className="email-attachment-actions">
                              {isPreviewable && (
                                <button
                                  className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email"
                                  onClick={(e) => { e.stopPropagation(); handlePreview(att); }}
                                  title={strings.email_preview || "Preview"}
                                >
                                  <MaterialIcon icon="visibility" size={14} />
                                </button>
                              )}
                              <button
                                className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email"
                                onClick={(e) => { e.stopPropagation(); handleDownload(att); }}
                                title={strings.email_download || "Download"}
                              >
                                <MaterialIcon icon="download" size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="email-attachments-loading">
                      <MaterialIcon icon="autorenew" size={16} className="email-refresh-spin" />
                      <span>{strings.email_loading_attachments || "Loading attachments..."}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="email-detail-body">
                <iframe
                  ref={iframeRef}
                  className="email-body-iframe"
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                  title="Email content"
                  scrolling="no"
                />
              </div>
              {threadEmails.length > 0 && (
                <div className="email-thread-list">
                  {threadEmails.map((threadEmail) => {
                    const tid = emailRecordId(threadEmail);
                    return (
                      <ThreadMessageCard
                        key={tid || threadEmail.internetMessageId || threadEmail.receivedAt}
                        message={threadEmail}
                        strings={strings}
                        formatFullDate={formatFullDate}
                        isBookmarked={!!(threadEmail.isTodoBookmarked || localBookmarkedIds.has(tid))}
                        onToggleBookmark={canBookmarkEmail(!!(threadEmail.isTodoBookmarked || localBookmarkedIds.has(tid))) ? handleToggleBookmark : undefined}
                        onReply={onReply ? (em) => handleReplyFrom("reply", em) : undefined}
                        onReplyAll={onReplyAll ? (em) => handleReplyFrom("replyAll", em) : undefined}
                        onForward={onForward ? (em) => handleReplyFrom("forward", em) : undefined}
                        expanded={expandedThreadIds.has(tid)}
                        onToggleExpand={() => {
                          setExpandedThreadIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(tid)) next.delete(tid);
                            else next.add(tid);
                            return next;
                          });
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : (!showCompanionTab && activeTab === "detail") ? (
          <div className="email-detail-empty">
            <MaterialIcon icon="mail_off" size={48} color="#ccc" className="email-detail-view__empty-icon" />
            <p className="email-detail-view__empty-text">
              {`${strings.email_no_messages || "No emails"} ${String(strings.selected || "selected").toLowerCase()}`}
            </p>
          </div>
        ) : null}
      </div>

      {/* Bottom tabs: switch between compose and detail */}
      {showBottomTabs && (
        <div className="email-bottom-tabs">
          <button
            className={`kj-tab email-bottom-tab${activeTab === "compose" ? " is-active" : ""}`}
            onClick={() => setActiveTab("compose")}
            title={composeTabLabel}
          >
            <MaterialIcon icon={composeTabIcon} size={14} />
            <span>{composeTabLabel}</span>
          </button>
          {showCompanionTab && (
            <button
              className={`kj-tab email-bottom-tab${activeTab === "detail" ? " is-active" : ""}`}
              onClick={() => setActiveTab("detail")}
              title={companionTab.label}
            >
              <MaterialIcon icon={companionTab.icon || "chat"} size={14} />
              <span className="email-bottom-tab-subject">{companionTab.label}</span>
            </button>
          )}
          {showViewedEmailTab && (
            <button
              className={`kj-tab email-bottom-tab${activeTab === "detail" ? " is-active" : ""}`}
              onClick={() => setActiveTab("detail")}
              title={email.subject || "Email"}
            >
              <MaterialIcon icon="mail" size={14} />
              <span className="email-bottom-tab-subject">{email.subject || (strings.email_no_subject || "(No subject)")}</span>
            </button>
          )}
          <button
            className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--danger email-bottom-tab-close"
            onClick={async () => {
              if (inlineComposeRef.current?.requestClose) {
                const closed = await inlineComposeRef.current.requestClose();
                if (!closed) setActiveTab("compose");
                return;
              }
              onCancelReply();
            }}
            title={strings.cancel || "Close"}
          >
            <MaterialIcon icon="close" size={14} />
          </button>
        </div>
      )}

      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          apiBaseUrl={apiBaseUrl}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

      {tagActionProject && ReactDOM.createPortal(
        <ProjectTagActionModal
          project={tagActionProject}
          projectColor={(tagActionProject && tagActionProject.themeColor) || (projectColorById && projectColorById.get(String(tagActionProject.id))) || null}
          onNavigate={onOpenProject ? () => {
            onOpenProject(tagActionProject, emailIdKey);
            setTagActionProject(null);
          } : undefined}
          onChange={onChangeProject ? () => {
            setTagActionProject(null);
            onChangeProject(email);
          } : undefined}
          onRemove={onRemoveProject ? () => {
            const bId = tagActionProject.id;
            setTagActionProject(null);
            onRemoveProject(email, bId);
          } : undefined}
          onClose={() => setTagActionProject(null)}
          strings={strings}
        />,
        document.body
      )}
    </div>
  );
});

export default EmailDetailView;
export { AttachmentPreviewModal };
